import { ParticipationRecordingButton } from "./components/ParticipationRecordingButton";
import { AiIcon } from "./components/AiIcon";
import { MobileTemplatePicker, MobileSelectorTemplateSettings } from "./components/MobileTemplateSettings";
import { eventFromPath, resolveEvent, publicEventListPath } from "./event-link";
import { BannerSlider, type BannerSlide } from "./components/BannerSlider";
import { SponsorSlideEditor } from "./components/SponsorSlideEditor";
import { BeaconEditor } from "./components/BeaconEditor";
import {
  useCallback,
  useId,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { api, ApiError, eventQuery } from "./api";
import type {
  Abstract,
  AiSpeakerProfile,
  CatalogItem,
  CatalogResource,
  Catalogs,
  Communication,
  CommunicationChannel,
  DashboardData,
  Event,
  EventSubevent,
  Exhibitor,
  ExhibitorStaff,
  Meeting,
  NavPage,
  Organizer,
  Participation,
  Permission,
  ReportData,
  Session,
  SessionDefaults,
  Speaker,
  User,
  WhatsappInbox,
} from "./types";
import { Modal } from "./components/Modal";
import { ParticipantAvatar } from "./components/ParticipantAvatar";
import { WhatsappChatModal } from "./components/WhatsappChatModal";
import { WhatsappInboxButton } from "./components/WhatsappInbox";
import { SessionWhatsappGroupModal } from "./components/SessionWhatsappGroupModal";
import { SessionWhatsappGroupChatModal } from "./components/SessionWhatsappGroupChatModal";
import { WhatsappIcon } from "./components/WhatsappIcon";
import { LiveTranslationCapture } from "./components/LiveTranslationCapture";
import { BrandLogo } from "./components/BrandLogo";
import { OrganizeUtilityControls } from "./components/OrganizeUtilityControls";
import { MaterialPreviewModal } from "./components/MaterialPreviewModal";
import { DashboardPage } from "./pages/DashboardPage";
import { SchedulePage } from "./pages/SchedulePage";
import { SpeakersPage } from "./pages/SpeakersPage";
import { AbstractsPage } from "./pages/AbstractsPage";
import { CommunicationsPage } from "./pages/CommunicationsPage";
import { MailPage } from "./pages/MailPage";
import { OrganigramPage } from "./pages/OrganigramPage";
import { SettingsPage } from "./pages/SettingsPage";
import { ReportsPage } from "./pages/ReportsPage";
import { LiveLocationsPage } from "./pages/LiveLocationsPage";
import { EventsPage } from "./pages/EventsPage";
import { MeetingsPage } from "./pages/MeetingsPage";
import { UsersPage } from "./pages/UsersPage";
import { OrganizersPage } from "./pages/OrganizersPage";
import { LoginPage } from "./pages/LoginPage";
import { FacultyPortal } from "./pages/FacultyPortal";
import { usePageTranslation } from "./hooks/usePageTranslation";
import { organizeUiCatalog } from "./generated/organizeUiCatalog";
import { mobileUiCatalog } from "./generated/mobileUiCatalog";
import Swal from "sweetalert2";
import { confirmDelete, deleteFailed } from "./utils/confirmDelete";
import {
  ActionIcon,
  IconActionButton,
  WhatsappActionButton,
} from "./components/AdminActions";

type ModalState =
  | { type: "session"; item?: Session; defaults?: SessionDefaults }
  | { type: "speaker"; item?: Speaker }
  | { type: "abstract"; item?: Abstract }
  | { type: "communication"; item?: Communication }
  | { type: "event"; item?: Event }
  | { type: "master"; resource: CatalogResource; item?: CatalogItem }
  | null;

type ThemeMode = "light" | "dark";
type SponsorPresentation = {
  banners?: BannerSlide[];
  name?: string | null;
  slogan?: string | null;
  logoUrl?: string | null;
  bannerUrl?: string | null;
  source?: string | null;
};

function MainSponsorFooter({ sponsor, context = "Main sponsor", fixed = false }: { sponsor?: SponsorPresentation | null; context?: string; fixed?: boolean }) {
  if (!sponsor?.name && !sponsor?.banners?.length) return null;
  sponsor = sponsor!;
  const image = sponsor.bannerUrl || sponsor.logoUrl;
  return (
    <>
      <footer className={`main-sponsor-footer${fixed ? " is-fixed" : ""}`} aria-label={sponsor.name ? `${context}: ${sponsor.name}` : context}>
        <div className="main-sponsor-copy">
          <span>{context}</span>
          {!sponsor.banners?.length && <strong>{sponsor.name}</strong>}
          {!sponsor.banners?.length && sponsor.slogan && <small>{sponsor.slogan}</small>}
        </div>
        <BannerSlider label={context} slides={sponsor.banners?.length ? sponsor.banners : image ? [{id:"legacy",imageUrl:image,alt:sponsor.name || context,durationSeconds:10}] : []} />
      </footer>
      {fixed && <div className="main-sponsor-footer-spacer" aria-hidden="true" />}
    </>
  );
}
type GlobalSearchKind =
  | "page"
  | "session"
  | "speaker"
  | "abstract"
  | "communication"
  | "exhibitor";
type GlobalSearchResult = {
  key: string;
  kind: GlobalSearchKind;
  id: number | string;
  title: string;
  detail: string;
  category: string;
};

const emptyCatalogs: Catalogs = {
  days: [],
  blocks: [],
  rooms: [],
  topics: [],
  modes: [],
  roles: [],
  exhibitors: [], exhibitorUsers: [],
  facultyTypes: [],
  eventStatuses: [],
  scientificTables: [],
  subevents: [],
  positioningMode: "general",
  beacons: [],
};

const navigation: Array<{
  page: NavPage;
  permission: Permission;
  section: "management" | "system";
  code: string;
  label: string;
}> = [
  {
    page: "dashboard",
    permission: "dashboard",
    section: "management",
    code: "IN",
    label: "Inicio",
  },
  {
    page: "organigram",
    permission: "organigram",
    section: "management",
    code: "OR",
    label: "Organigrama",
  },
  {
    page: "schedule",
    permission: "schedule",
    section: "management",
    code: "AG",
    label: "Agenda",
  },
  {
    page: "speakers",
    permission: "speakers",
    section: "management",
    code: "FA",
    label: "Faculty",
  },
  {
    page: "abstracts",
    permission: "abstracts",
    section: "management",
    code: "TR",
    label: "Trabajos",
  },
  {
    page: "meetings",
    permission: "meetings",
    section: "management",
    code: "ME",
    label: "Meetings",
  },
  {
    page: "events",
    permission: "events",
    section: "system",
    code: "EV",
    label: "Eventos",
  },
  {
    page: "organizers",
    permission: "events",
    section: "system",
    code: "OG",
    label: "Organizadores",
  },
  {
    page: "settings",
    permission: "settings",
    section: "system",
    code: "AJ",
    label: "Ajustes",
  },
  {
    page: "users",
    permission: "users",
    section: "system",
    code: "US",
    label: "Usuarios",
  },
  {
    page: "communications",
    permission: "communications",
    section: "system",
    code: "CO",
    label: "Comunicaciones",
  },
  {
    page: "mail",
    permission: "communications",
    section: "system",
    code: "CR",
    label: "Correos",
  },
  {
    page: "reports",
    permission: "reports",
    section: "system",
    code: "RE",
    label: "Reportes",
  },
  {
    page: "locations",
    permission: "reports",
    section: "system",
    code: "EB",
    label: "Geolocalización",
  },
];

function userCanAccess(user: User | null, permission: Permission) {
  return Boolean(
    user && (user.role === "admin" || user.permissions.includes(permission)),
  );
}
function firstAllowedPage(user: User) {
  return (
    navigation.find((item) => userCanAccess(user, item.permission))?.page ||
    "dashboard"
  );
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [page, setPage] = useState<NavPage>("dashboard");
  const [events, setEvents] = useState<Event[]>([]);
  const eventReference = useMemo(eventFromPath, []);
  const [eventLinkError, setEventLinkError] = useState("");
  const [eventId, setEventId] = useState(1);
  const selectedEventRef = useRef(eventId);
  selectedEventRef.current = eventId;
  const eventLoadSequence = useRef(0);
  const [loadedEventId, setLoadedEventId] = useState<number | null>(null);
  const [catalogs, setCatalogs] = useState<Catalogs>(emptyCatalogs);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [abstracts, setAbstracts] = useState<Abstract[]>([]);
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [reports, setReports] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<ModalState>(null);
  const [audioSession, setAudioSession] = useState<Session | null>(null);
  const [saving, setSaving] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [aiProfile, setAiProfile] = useState<AiSpeakerProfile | null>(null);
  const [aiLoadingSpeakerId, setAiLoadingSpeakerId] = useState<number | null>(
    null,
  );
  const [whatsappInbox, setWhatsappInbox] = useState<WhatsappInbox | null>(
    null,
  );
  const [whatsappInboxOpen, setWhatsappInboxOpen] = useState(false);
  const [whatsappChatSpeakerId, setWhatsappChatSpeakerId] = useState<
    number | null
  >(null);
  const [whatsappGroupSession, setWhatsappGroupSession] =
    useState<Session | null>(null);
  const [whatsappGroupChatSession, setWhatsappGroupChatSession] =
    useState<Session | null>(null);
  const [whatsappRevision, setWhatsappRevision] = useState(0);
  const [whatsappRealtimeConnected, setWhatsappRealtimeConnected] =
    useState(false);
  const [siteLanguage, setSiteLanguage] = useState("es");
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");
  const [theme, setTheme] = useState<ThemeMode>(() => {
    const saved = window.localStorage.getItem("organize-theme");
    if (saved === "light" || saved === "dark") return saved;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  const activeEvent = useMemo(
    () => events.find((event) => event.id === eventId) || events[0],
    [events, eventId],
  );
  const activeEvents = useMemo(
    () => events.filter((event) => Boolean(event.organizeEnabled)),
    [events],
  );
  const callForScienceEnabled = Boolean(activeEvent?.abstractsEnabled);
  const visibleNavigation = useMemo(
    () => navigation.filter((item) => item.page !== "mail" && userCanAccess(user, item.permission) && (item.page !== "organizers" || user?.superAdmin) && (item.page !== "abstracts" || callForScienceEnabled)),
    [user, callForScienceEnabled],
  );
  useEffect(() => {
    if (page === "abstracts" && !callForScienceEnabled) {
      setPage(visibleNavigation[0]?.page || "dashboard");
      setModal(null);
    }
  }, [page, callForScienceEnabled, visibleNavigation]);
  const globalSearchResults = useMemo<GlobalSearchResult[]>(() => {
    const query = normalizeSearch(globalSearchQuery);
    if (query.length < 2) return [];
    const matches = (value: unknown) =>
      normalizeSearch(String(value || "")).includes(query);
    const results: GlobalSearchResult[] = [];
    for (const item of visibleNavigation) {
      if (matches(item.label + " " + item.page)) {
        results.push({
          key: "page-" + item.page,
          kind: "page",
          id: item.page,
          title: item.label,
          detail: "Ir a esta sección",
          category: "Sección",
        });
      }
    }
    if (userCanAccess(user, "schedule")) {
      for (const session of sessions) {
        if (
          matches(
            [
              session.title,
              session.topicName,
              session.speakers,
              session.dayName,
              session.roomName,
              session.subeventTitle,
            ].join(" "),
          )
        ) {
          results.push({
            key: "session-" + session.id,
            kind: "session",
            id: session.id,
            title: session.title,
            detail:
              session.dayName +
              " · " +
              session.startTime +
              " · " +
              session.roomName,
            category: "Sesión",
          });
        }
      }
    }
    if (userCanAccess(user, "speakers")) {
      for (const speaker of speakers) {
        if (
          matches(
            [
              speaker.firstName,
              speaker.lastName,
              speaker.email,
              speaker.institution,
              speaker.country,
              speaker.category,
            ].join(" "),
          )
        ) {
          results.push({
            key: "speaker-" + speaker.id,
            kind: "speaker",
            id: speaker.id,
            title: speaker.firstName + " " + speaker.lastName,
            detail:
              [speaker.institution, speaker.country].filter(Boolean).join(" · ") ||
              speaker.email,
            category: "Faculty",
          });
        }
      }
    }
    if (callForScienceEnabled && userCanAccess(user, "abstracts")) {
      for (const item of abstracts) {
        if (
          matches(
            [
              item.code,
              item.title,
              item.submitterName,
              item.category,
              item.country,
            ].join(" "),
          )
        ) {
          results.push({
            key: "abstract-" + item.id,
            kind: "abstract",
            id: item.id,
            title: item.code + " · " + item.title,
            detail: item.submitterName + " · " + item.status,
            category: "Trabajo",
          });
        }
      }
    }
    if (userCanAccess(user, "communications")) {
      for (const item of communications) {
        if (matches([item.subject, item.body, item.status].join(" "))) {
          results.push({
            key: "communication-" + item.id,
            kind: "communication",
            id: item.id,
            title: item.subject,
            detail: item.status,
            category: "Comunicación",
          });
        }
      }
    }
    if (userCanAccess(user, "settings")) {
      for (const item of catalogs.exhibitors) {
        if (
          matches(
            [item.name, item.standCode, item.slogan, item.contactEmail].join(
              " ",
            ),
          )
        ) {
          results.push({
            key: "exhibitor-" + item.id,
            kind: "exhibitor",
            id: item.id,
            title: item.name,
            detail: item.standCode ? "Stand " + item.standCode : item.slogan,
            category: "Expositor",
          });
        }
      }
    }
    return results.slice(0, 60);
  }, [
    callForScienceEnabled,
    abstracts,
    catalogs.exhibitors,
    communications,
    globalSearchQuery,
    sessions,
    speakers,
    user,
    visibleNavigation,
  ]);
  const siteLanguages = useMemo(() => {
    const configured = [
      ...new Set(
        (activeEvent?.languages || "es")
          .split(/[,;\s]+/)
          .map((item) => item.trim().toLowerCase())
          .filter(Boolean),
      ),
    ];
    return configured;
  }, [activeEvent?.languages]);
  const interfaceLanguages = useMemo(
    () => siteLanguages,
    [siteLanguages],
  );
  const primaryLanguage = siteLanguages[0] || "es";

  useEffect(() => {
    if (!activeEvent) return;
    const saved =
      window.localStorage.getItem(`organize-language-${activeEvent.id}`) || "";
    setSiteLanguage(interfaceLanguages.includes(saved) ? saved : primaryLanguage);
  }, [activeEvent?.id, primaryLanguage, interfaceLanguages.join(",")]);
  const pageTranslation = usePageTranslation(
    activeEvent?.id || eventId,
    siteLanguage,
    Boolean(activeEvent?.aiOrganizeAvailable),
  );
  useEffect(() => {
    if (!activeEvent?.id || !activeEvent.aiOrganizeAvailable) return;
    api("/api/translations/prime", {
      method: "POST",
      body: JSON.stringify({ eventId: activeEvent.id, organizeTexts: organizeUiCatalog, mobileTexts: mobileUiCatalog }),
    }).catch(() => undefined);
  }, [activeEvent?.id, activeEvent?.languages, activeEvent?.aiOrganizeAvailable]);
  const translationErrorShown = useRef("");
  useEffect(() => {
    if (
      !pageTranslation.error ||
      translationErrorShown.current === pageTranslation.error
    )
      return;
    translationErrorShown.current = pageTranslation.error;
    void Swal.fire({
      icon: "error",
      title: "No se pudo traducir la página",
      text: pageTranslation.error,
      confirmButtonText: "Entendido",
      confirmButtonColor: activeEvent?.primaryColor || "#169c98",
    });
  }, [pageTranslation.error, activeEvent?.primaryColor]);
  useEffect(() => {
    translationErrorShown.current = "";
  }, [siteLanguage, eventId]);

  useEffect(() => {
    const percent = Math.max(
      85,
      Math.min(140, Number(activeEvent?.fontScalePercent || 100)),
    );
    document.documentElement.style.setProperty(
      "--site-font-scale",
      String(percent / 100),
    );
    return () => {
      document.documentElement.style.removeProperty("--site-font-scale");
    };
  }, [activeEvent?.fontScalePercent]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    window.localStorage.setItem("organize-theme", theme);
  }, [theme]);

  useEffect(() => {
    const openSearch = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setGlobalSearchOpen(true);
      }
    };
    window.addEventListener("keydown", openSearch);
    return () => window.removeEventListener("keydown", openSearch);
  }, []);

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3200);
  }, []);

  const loadEverything = useCallback(
    async (selectedEvent: number) => {
      if (!user || selectedEvent !== selectedEventRef.current) return;
      const requestId = ++eventLoadSequence.current;
      const isCurrent = () => requestId === eventLoadSequence.current && selectedEvent === selectedEventRef.current;
      setLoading(true);
      const query = eventQuery(selectedEvent);
      try {
        const [
          dashboardData,
          catalogData,
          sessionData,
          speakerData,
          abstractData,
          communicationData,
          meetingData,
          reportData,
        ] = await Promise.all([
          userCanAccess(user, "dashboard")
            ? api<DashboardData>(`/api/dashboard?${query}`)
            : Promise.resolve(null),
          api<Catalogs>(`/api/catalogs?${query}`),
          userCanAccess(user, "schedule") || userCanAccess(user, "speakers")
            ? api<{ items: Session[] }>(`/api/sessions?${query}`)
            : Promise.resolve({ items: [] }),
          userCanAccess(user, "speakers") ||
          userCanAccess(user, "schedule") ||
          userCanAccess(user, "meetings")
            ? api<{ items: Speaker[] }>(`/api/speakers?${query}`)
            : Promise.resolve({ items: [] }),
          userCanAccess(user, "abstracts")
            ? api<{ items: Abstract[] }>(`/api/abstracts?${query}`)
            : Promise.resolve({ items: [] }),
          userCanAccess(user, "communications") ||
          userCanAccess(user, "speakers") ||
          userCanAccess(user, "organigram")
            ? api<{ items: Communication[] }>(`/api/communications?${query}`)
            : Promise.resolve({ items: [] }),
          userCanAccess(user, "meetings")
            ? api<{ items: Meeting[] }>(`/api/meetings?${query}`)
            : Promise.resolve({ items: [] }),
          userCanAccess(user, "reports")
            ? api<ReportData>(`/api/reports?${query}`)
            : Promise.resolve(null),
        ]);
        if (!isCurrent()) return;
        setLoadedEventId(selectedEvent);
        setDashboard(dashboardData);
        setCatalogs(catalogData);
        setSessions(sessionData.items);
        setSpeakers(speakerData.items);
        setAbstracts(abstractData.items.filter((item) => item.eventId === selectedEvent));
        setCommunications(communicationData.items);
        setMeetings(meetingData.items);
        setReports(reportData);
      } catch (error) {
        if (!isCurrent()) return;
        if (error instanceof ApiError && error.status === 401) setUser(null);
        else
          showToast(
            error instanceof Error
              ? error.message
              : "No se pudieron cargar los datos",
          );
      } finally {
        if (isCurrent()) setLoading(false);
      }
    },
    [showToast, user],
  );

  const loadWhatsappInbox = useCallback(async () => {
    if (
      !user ||
      (!userCanAccess(user, "speakers") &&
        !userCanAccess(user, "communications"))
    )
      return;
    try {
      setWhatsappInbox(
        await api<WhatsappInbox>(
          `/api/whatsapp/conversations?${eventQuery(eventId)}`,
        ),
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setUser(null);
    }
  }, [eventId, user]);

  useEffect(() => {
    api<{ user: User }>("/api/auth/me")
      .then(async ({ user: nextUser }) => {
        if (eventReference.includes("/")) await api(publicEventListPath(eventReference));
        setUser(nextUser);
        if (nextUser.role === "faculty") {
          if (eventReference) {
            const { events: available } = await api<{ events: Event[] }>("/api/public/events");
            const target = resolveEvent(available, eventReference, item => item.id, item => [item.shortName, item.name]);
            if (!target || target.id !== nextUser.eventId) setEventLinkError("Tu sesión corresponde a otro evento. Cerrá sesión para ingresar al evento solicitado.");
          }
          return;
        }
        setPage(firstAllowedPage(nextUser));
        const { events: eventList } = await api<{ events: Event[] }>(
          "/api/events",
        );
        const target = eventReference ? resolveEvent(eventList.filter(item => Boolean(item.organizeEnabled)), eventReference, item => item.id, item => [item.shortName, item.name]) : undefined;
        if (eventReference && !target) { setEventLinkError("El evento no existe o no está disponible para tu usuario."); return; }
        setEvents(target ? [target] : eventList);
        const initialEvent = target ||
          eventList.find((item) => Boolean(item.organizeEnabled)) ||
          eventList[0];
        if (initialEvent) setEventId(initialEvent.id);
      })
      .catch(() => setUser(null))
      .finally(() => {
        setEventsLoading(false);
        setAuthLoading(false);
      });
  }, []);

  useEffect(() => {
    if (user && events.length) loadEverything(eventId);
  }, [user, events.length, eventId, loadEverything]);

  useEffect(() => {
    if (
      !user ||
      (!userCanAccess(user, "speakers") &&
        !userCanAccess(user, "communications"))
    )
      return;
    loadWhatsappInbox();
    const timer = window.setInterval(loadWhatsappInbox, 15_000);
    const refreshOnFocus = () => loadWhatsappInbox();
    window.addEventListener("focus", refreshOnFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshOnFocus);
    };
  }, [loadWhatsappInbox, user]);

  useEffect(() => {
    if (
      !user ||
      (!userCanAccess(user, "speakers") &&
        !userCanAccess(user, "communications"))
    )
      return;
    const stream = new EventSource(
      `/api/whatsapp/events?${eventQuery(eventId)}`,
    );
    stream.onopen = () => setWhatsappRealtimeConnected(true);
    stream.onerror = () => setWhatsappRealtimeConnected(false);
    const refresh = () => {
      setWhatsappRevision((current) => current + 1);
      loadWhatsappInbox();
    };
    stream.addEventListener("whatsapp", refresh);
    return () => {
      stream.removeEventListener("whatsapp", refresh);
      stream.close();
      setWhatsappRealtimeConnected(false);
    };
  }, [eventId, loadWhatsappInbox, user]);

  async function handleAuthenticated(
    nextUser: User,
    preferredEventId?: number,
  ) {
    setEventsLoading(true);
    if (eventReference.includes("/")) await api(publicEventListPath(eventReference));
    setUser(nextUser);
    if (nextUser.role === "faculty") {
      setEventsLoading(false);
      return;
    }
    setPage(firstAllowedPage(nextUser));
    try {
      const { events: eventList } = await api<{ events: Event[] }>("/api/events");
      const target = eventReference ? resolveEvent(eventList.filter(item => Boolean(item.organizeEnabled)), eventReference, item => item.id, item => [item.shortName, item.name]) : undefined;
      if (eventReference && !target) { setEventLinkError("El evento no existe o no está disponible para tu usuario."); return; }
      setEventLinkError("");
      setEvents(target ? [target] : eventList);
      const preferred = target ||
        eventList.find((item) => item.id === preferredEventId) ||
        eventList.find((item) => Boolean(item.organizeEnabled)) ||
        eventList[0];
      if (preferred) setEventId(preferred.id);
    } finally {
      setEventsLoading(false);
    }
  }

  async function logout() {
    await api("/api/auth/logout", { method: "POST" }).catch(() => undefined);
    setUser(null);
    setEvents([]);
    setEventsLoading(false);
    setWhatsappInbox(null);
  }

  function navigate(nextPage: NavPage) {
    if (nextPage === "abstracts" && !callForScienceEnabled) return;
    setPage(nextPage);
    setMobileNav(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function openGlobalSearchResult(result: GlobalSearchResult) {
    setGlobalSearchOpen(false);
    setGlobalSearchQuery("");
    if (result.kind === "page") {
      navigate(result.id as NavPage);
      return;
    }
    if (result.kind === "session") {
      const item = sessions.find((candidate) => candidate.id === result.id);
      if (item) setModal({ type: "session", item });
      return;
    }
    if (result.kind === "speaker") {
      const item = speakers.find((candidate) => candidate.id === result.id);
      if (item) setModal({ type: "speaker", item });
      return;
    }
    if (result.kind === "abstract") {
      const item = abstracts.find((candidate) => candidate.id === result.id);
      if (item) setModal({ type: "abstract", item });
      return;
    }
    if (result.kind === "communication") {
      const item = communications.find(
        (candidate) => candidate.id === result.id,
      );
      if (item) setModal({ type: "communication", item });
      return;
    }
    const item = catalogs.exhibitors.find(
      (candidate) => candidate.id === result.id,
    );
    if (item) setModal({ type: "master", resource: "exhibitors", item });
  }

  async function saveModal(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!modal) return;
    setSaving(true);
    const form = new FormData(event.currentTarget);
    const values = Object.fromEntries(form.entries());
    for (const key of [
      "abstractSubmissionOpensAt",
      "abstractSubmissionClosesAt",
      "reviewerAssignmentAt",
      "reviewStartsAt",
    ])
      if (values[key])
        values[key] = new Date(String(values[key])).toISOString();
    const itemId = modal.item?.id;
    let url: string;
    let body: Record<string, unknown> = { ...values, eventId };
    if (modal.type === "event") {
      url = `/api/events${itemId ? `/${itemId}` : ""}`;
      body = {
        ...values,
        organizeEnabled: form.has("organizeEnabled"),
        mobileEnabled: form.has("mobileEnabled"),
        mobileInternalChatEnabled: Boolean(events.find((item) => item.id === itemId)?.mobileInternalChatEnabled),
        mobileRegistrationRequired: Boolean(events.find((item) => item.id === itemId)?.mobileRegistrationRequired),
        mobileRegistrationMessage: events.find((item) => item.id === itemId)?.mobileRegistrationMessage || "",
        mobileShowParticipantNames: form.has("mobileShowParticipantNames"),
        mobileScientificWorksPublished: form.has("mobileScientificWorksPublished"),
        mobileScientificShowAbstract: form.has("mobileScientificShowAbstract"),
        mobileScientificShowPresentation: form.has("mobileScientificShowPresentation"),
        mobileScientificAllowPdfDownload: form.has("mobileScientificAllowPdfDownload"),
        abstractsEnabled: form.has("abstractsEnabled"),
        meetingLive: form.has("meetingLive"),
        mainSponsorAppEntry: form.has("mainSponsorAppEntry"),
        mainSponsorPdfProgram: form.has("mainSponsorPdfProgram"),
        mainSponsorProgramFooter: form.has("mainSponsorProgramFooter"),
        mainSponsorConciergeFooter: form.has("mainSponsorConciergeFooter"),
        mainSponsorOrganizeFooter: form.has("mainSponsorOrganizeFooter"),
        mainSponsorCall4ScienceFooter: form.has("mainSponsorCall4ScienceFooter"),
      };
    } else if (modal.type === "master") {
      url = `/api/catalogs/${modal.resource}${itemId ? `/${itemId}` : ""}`;
      body = {
        ...values,
        eventId,
        ...(["rooms", "exhibitors"].includes(modal.resource) ? { beacons: JSON.parse(String(form.get("beacons") || "[]")) } : {}),
        isStart: form.has("isStart"),
        abstractsLinked: form.has("abstractsLinked"),
        showInProgram: form.has("showInProgram"),
        fullAccess: form.has("fullAccess"),
        active: form.has("active"),
        ...(modal.resource === "exhibitors" ? { beaconEmissionEnabled: form.has("beaconEmissionEnabled"), exhibitorUserIds: form.getAll("exhibitorUserIds").map(Number) } : {}),
      };
    } else {
      const resource =
        modal.type === "speaker"
          ? "speakers"
          : modal.type === "abstract"
            ? "abstracts"
            : modal.type === "session"
              ? "sessions"
              : "communications";
      url = `/api/${resource}${itemId ? `/${itemId}` : ""}`;
      if (modal.type === "communication")
        body.channels = form.getAll("channels");
    }
    try {
      if (modal.type === "event") {
        for (const image of [
          {
            input: "whatsappIcon",
            field: "whatsappIconData",
            remove: "removeWhatsappIcon",
            max: 2_000_000,
            label: "El icono de WhatsApp",
          },
          {
            input: "emailHeader",
            field: "emailHeaderData",
            remove: "removeEmailHeader",
            max: 5_000_000,
            label: "El encabezado de email",
          },
          {
            input: "eventBanner",
            field: "bannerData",
            remove: "removeEventBanner",
            max: 5_000_000,
            label: "El banner del evento",
          },
          {
            input: "eventSplash",
            field: "splashData",
            remove: "removeEventSplash",
            max: 5_000_000,
            label: "La imagen de splash",
          },
        ]) {
          const file = form.get(image.input);
          delete body[image.input];
          delete body[image.remove];
          if (file instanceof File && file.size) {
            if (file.size > image.max)
              throw new Error(
                `${image.label} no puede superar los ${image.max / 1_000_000} MB`,
              );
            if (!/image\/(jpeg|png|webp)/.test(file.type))
              throw new Error(`${image.label} debe ser JPG, PNG o WebP`);
            body[image.field] = await fileToDataUrl(file);
          } else if (form.has(image.remove)) body[image.field] = "";
        }
        const testAudio = form.get("translationTestAudio");
        delete body.translationTestAudio;
        body.removeTranslationTestAudio = form.has(
          "removeTranslationTestAudio",
        );
        if (testAudio instanceof File && testAudio.size) {
          if (testAudio.size > 8_000_000)
            throw new Error("El audio de prueba no puede superar los 8 MB");
          if (
            !/^audio\/(mpeg|mp3|wav|wave|x-wav|webm|mp4|m4a)$/i.test(
              testAudio.type,
            ) &&
            !/\.(mp3|wav|webm|m4a)$/i.test(testAudio.name)
          )
            throw new Error("El audio de prueba debe ser MP3, WAV, WebM o M4A");
          body.translationTestAudioData = await fileToDataUrl(testAudio);
          body.translationTestAudioName = testAudio.name;
          body.removeTranslationTestAudio = false;
        }
      }
      if (modal.type === "speaker") {
        const photo = form.get("photo");
        delete body.photo;
        if (photo instanceof File && photo.size) {
          if (photo.size > 2_000_000)
            throw new Error("La foto no puede superar los 2 MB");
          if (!/image\/(jpeg|png|webp)/.test(photo.type))
            throw new Error("La foto debe ser JPG, PNG o WebP");
          body.photoData = await fileToDataUrl(photo);
        }
      }
      if (modal.type === "master" && modal.resource === "exhibitors") {
        const current =
          modal.item && "standCode" in modal.item ? modal.item : undefined;
        for (const asset of [
          {
            input: "logo",
            field: "logoData",
            remove: "removeLogo",
            current: current?.logoUrl || "",
            max: 2_000_000,
            label: "El logo",
          },
          {
            input: "banner",
            field: "bannerData",
            remove: "removeBanner",
            current: current?.bannerUrl || "",
            max: 5_000_000,
            label: "El banner",
          },
        ]) {
          const file = form.get(asset.input);
          delete body[asset.input];
          delete body[asset.remove];
          if (file instanceof File && file.size) {
            if (file.size > asset.max)
              throw new Error(
                `${asset.label} no puede superar los ${asset.max / 1_000_000} MB`,
              );
            if (!/image\/(jpeg|png|webp)/.test(file.type))
              throw new Error(`${asset.label} debe ser JPG, PNG o WebP`);
            body[asset.field] = await fileToDataUrl(file);
          } else
            body[asset.field] = form.has(asset.remove) ? "" : asset.current;
        }
      }
      const result = await api<{ id: number }>(url, {
        method: itemId ? "PUT" : "POST",
        body: JSON.stringify(body),
      });
      setModal(null);
      showToast(itemId ? "Cambios guardados" : "Registro creado correctamente");
      if (modal.type === "event") {
        const { events: eventList } = await api<{ events: Event[] }>(
          "/api/events",
        );
        setEvents(eventList);
        if (!itemId) setEventId(result.id);
        else if (itemId === eventId) await loadEverything(eventId);
      } else {
        await loadEverything(eventId);
      }
      if (modal.type === "speaker" && !itemId && activeEvent.aiOrganizeAvailable) {
        const confirmation = await Swal.fire({
          icon: "question",
          title: "¿Completar el perfil con IA?",
          text: "La IA buscará una foto pública y preparará un resumen profesional y académico. Solo se enviaran nombre, institucion, pais y categoria a la plataforma de IA configurada para esta funcion.",
          showCancelButton: true,
          confirmButtonText: "Completar con IA",
          cancelButtonText: "Ahora no",
          confirmButtonColor: "#169c98",
          cancelButtonColor: "#708090",
          reverseButtons: true,
          focusCancel: true,
        });
        if (confirmation.isConfirmed) await enrichSpeakerWithAI(result.id);
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  }

  async function removeEvent(item: Event) {
    if (!await confirmDelete(item.name, "Se eliminará el evento y todos sus datos asociados. Esta acción no se puede deshacer.")) return;
    try {
      await api(`/api/events/${item.id}`, { method: "DELETE" });
      const { events: eventList } = await api<{ events: Event[] }>(
        "/api/events",
      );
      setEvents(eventList);
      if (item.id === eventId && eventList[0]) setEventId(eventList[0].id);
      showToast("Evento eliminado");
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : "No se pudo eliminar el evento",
      );
      await deleteFailed(error, "No se pudo eliminar el evento");
    }
  }

  async function removeResource(
    resource: string,
    item: { id: number },
    label: string,
  ) {
    if (!await confirmDelete(label)) return;
    try {
      await api(`/api/${resource}/${item.id}?${eventQuery(eventId)}`, {
        method: "DELETE",
      });
      if (resource === "speakers") setModal(null);
      showToast("Registro eliminado");
      await loadEverything(eventId);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "No se pudo eliminar");
      await deleteFailed(error);
    }
  }

  async function removeSession(item: Session) {
    const result = await Swal.fire({
      icon: "warning",
      title: "¿Eliminar esta sesión?",
      text: `Se eliminará “${item.title}” y toda la información asociada. Esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c13f49",
      cancelButtonColor: "#708090",
      reverseButtons: true,
      focusCancel: true,
    });
    if (!result.isConfirmed) return;
    try {
      await api(`/api/sessions/${item.id}?${eventQuery(eventId)}`, {
        method: "DELETE",
      });
      setModal(null);
      showToast("Sesión eliminada");
      await loadEverything(eventId);
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "No se pudo eliminar",
        text:
          error instanceof Error
            ? error.message
            : "No se pudo eliminar la sesión",
        confirmButtonText: "Entendido",
        confirmButtonColor: activeEvent.primaryColor || "#169c98",
      });
    }
  }

  async function createMeeting(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    const form = new FormData(event.currentTarget);
    try {
      await api("/api/meetings", {
        method: "POST",
        body: JSON.stringify({
          eventId,
          title: form.get("title"),
          startsAt: form.get("startsAt"),
          durationMinutes: form.get("durationMinutes"),
          roomName: form.get("roomName"),
          participantIds: form.getAll("participantIds").map(Number),
          sendInvitations: form.has("sendInvitations"),
        }),
      });
      showToast("Meeting programado");
      await loadEverything(eventId);
    } catch (error) {
      if ((error as {status?: number})?.status === 499) return ;
      showToast(
        error instanceof Error
          ? error.message
          : "No se pudo programar el meeting",
      );
    } finally {
      setSaving(false);
    }
  }

  async function updateMeetingStatus(
    meeting: Meeting,
    status: Meeting["status"],
  ) {
    try {
      await api(`/api/meetings/${meeting.id}`, {
        method: "PUT",
        body: JSON.stringify({ status }),
      });
      await loadEverything(eventId);
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : "No se pudo actualizar el meeting",
      );
    }
  }
  async function removeMeeting(meeting: Meeting) {
    if (!await confirmDelete(meeting.title, "Se eliminará el meeting y sus invitaciones asociadas.")) return;
    try {
      await api(`/api/meetings/${meeting.id}`, { method: "DELETE" });
      showToast("Meeting eliminado");
      await loadEverything(eventId);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "No se pudo eliminar");
      await deleteFailed(error, "No se pudo eliminar el meeting");
    }
  }

  async function sendCommunicationToSpeaker(
    speaker: Speaker,
    communicationId: number,
  ) {
    try {
      const result = await api<{
        testMode?: boolean;
        results: Array<{
          channel: CommunicationChannel;
          status: "sent" | "error";
          error: string | null;
        }>;
      }>(`/api/speakers/${speaker.id}/send-communication`, {
        method: "POST",
        body: JSON.stringify({ eventId, communicationId }),
      });
      if (result.testMode) { await loadEverything(eventId); return false; }
      const sent = result.results
        .filter((delivery) => delivery.status === "sent")
        .map((delivery) => communicationChannelLabel(delivery.channel));
      const failed = result.results
        .filter((delivery) => delivery.status === "error")
        .map((delivery) => communicationChannelLabel(delivery.channel));
      showToast(
        failed.length
          ? `Enviado por ${sent.join(" y ")}. Falló: ${failed.join(", ")}`
          : `Enviado simultáneamente por ${sent.join(" y ")}`,
      );
      await loadEverything(eventId);
      return sent.length > 0;
    } catch (error) {
      if ((error as {status?: number})?.status === 499) return false;
      showToast(
        error instanceof Error
          ? error.message
          : "No se pudo enviar el comunicado",
      );
      return false;
    }
  }

  async function enrichSpeakerWithAI(speakerId: number) {
    setAiLoadingSpeakerId(speakerId);
    showToast(
      "La IA está verificando identidad, foto y antecedentes públicos…",
    );
    try {
      const profile = await api<AiSpeakerProfile>(
        `/api/speakers/${speakerId}/ai-enrichment`,
        { method: "POST", body: JSON.stringify({ eventId }) },
      );
      setAiProfile(profile);
      await loadEverything(eventId);
      showToast(
        profile.matchedPerson
          ? "Perfil profesional recuperado"
          : "La IA no pudo confirmar la identidad con suficiente evidencia",
      );
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : "No se pudo recuperar el perfil con IA",
      );
    } finally {
      setAiLoadingSpeakerId(null);
    }
  }

  async function applyAiPhoto() {
    if (!aiProfile?.photoUrl) return;
    try {
      await api(`/api/speakers/${aiProfile.speakerId}/apply-ai-photo`, {
        method: "POST",
        body: JSON.stringify({ eventId }),
      });
      await loadEverything(eventId);
      setAiProfile(null);
      showToast("Foto sugerida aplicada al perfil");
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : "No se pudo aplicar la foto sugerida",
      );
    }
  }

  async function uploadPresentation(
    speaker: Speaker,
    participation: Participation,
    file: File,
  ) {
    if (!participation.id) return;
    try {
      if (file.size > 15_000_000)
        throw new Error("La presentación no puede superar los 15 MB");
      if (!/\.(pdf|ppt|pptx)$/i.test(file.name))
        throw new Error("La presentación debe ser PDF, PPT o PPTX");
      const data = await fileToDataUrl(file);
      await api(
        `/api/speakers/${speaker.id}/participations/${participation.id}/presentation`,
        {
          method: "POST",
          body: JSON.stringify({
            eventId,
            fileName: file.name,
            mimeType: file.type,
            data,
          }),
        },
      );
      showToast("Presentación cargada correctamente");
      await loadEverything(eventId);
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : "No se pudo cargar la presentación",
      );
    }
  }

  async function sendPresentationByWhatsapp(
    speaker: Speaker,
    participation: Participation,
  ) {
    if (!participation.id) return;
    try {
      await api(
        `/api/speakers/${speaker.id}/participations/${participation.id}/presentation/send-whatsapp`,
        { method: "POST", body: JSON.stringify({ eventId }) },
      );
      showToast("Presentación enviada por WhatsApp");
      await Promise.all([loadWhatsappInbox(), loadEverything(eventId)]);
    } catch (error) {
      if ((error as {status?: number})?.status === 499) return ;
      showToast(
        error instanceof Error
          ? error.message
          : "No se pudo enviar la presentación",
      );
    }
  }

  async function updateAbstractStatus(
    item: Abstract,
    status: Abstract["status"],
  ) {
    try {
      await api(`/api/abstracts/${item.id}`, {
        method: "PUT",
        body: JSON.stringify({
          ...item,
          status,
          eventId,
          ...(status === "pending" ? { scientificTableId: null } : {}),
        }),
      });
      showToast(
        status === "accepted"
          ? "Trabajo aceptado"
          : status === "rejected"
            ? "Trabajo rechazado"
            : "Trabajo restablecido a pendiente",
      );
      await loadEverything(eventId);
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : "No se pudo actualizar",
      );
    }
  }

  async function uploadAndCorrectAbstractPdf(item: Abstract, file: File) {
    try {
      if (
        file.type !== "application/pdf" &&
        !file.name.toLowerCase().endsWith(".pdf")
      )
        throw new Error("El trabajo debe estar en formato PDF");
      if (file.size > 15_000_000)
        throw new Error("El PDF no puede superar los 15 MB");
      const data = await fileToDataUrl(file);
      const result = await api<{ score: number }>(
        `/api/abstracts/${item.id}/pdf-ai-review`,
        {
          method: "POST",
          body: JSON.stringify({ eventId, fileName: file.name, data }),
        },
      );
      showToast(
        `PDF cargado y corregido por IA · Puntaje ${result.score.toFixed(1)}`,
      );
      await loadEverything(eventId);
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : "No se pudo corregir el PDF",
      );
    }
  }

  // Existing images stay on the server unless the form replaces or removes them.
  function eventSettingsValues(currentEvent: Event | undefined) {
    const values: Record<string, unknown> = { ...currentEvent };
    for (const key of [
      "bannerData", "splashData", "emailHeaderData", "whatsappIconData",
      "callForScienceHeroImage",
      "mainSponsorLogoUrl", "mainSponsorBannerUrl", "mainSponsorBanners",
    ]) delete values[key];
    return values;
  }

  async function saveEventSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    const form = new FormData(event.currentTarget);
    const values = Object.fromEntries(form.entries());
    for (const key of [
      "abstractSubmissionOpensAt",
      "abstractSubmissionClosesAt",
      "reviewerAssignmentAt",
      "reviewStartsAt",
    ])
      if (values[key])
        values[key] = new Date(String(values[key])).toISOString();
    try {
      const currentEvent = events.find((item) => item.id === eventId);
      const body: Record<string, unknown> = {
        ...eventSettingsValues(currentEvent),
        ...values,
        subevents: currentEvent?.subevents || [],
        emailFooter: currentEvent?.emailFooter || "",
        organizeEnabled: form.has("organizeEnabled"),
        mobileEnabled: form.has("mobileEnabled"),
        mobileInternalChatEnabled: form.has("mobileInternalChatEnabled"),
        mobileRegistrationRequired: form.has("mobileRegistrationRequired"),
        abstractsEnabled: form.has("abstractsEnabled"),
        meetingLive: form.has("meetingLive"),
        aiReviewerEnabled: currentEvent?.aiJuryAvailable
          ? form.has("aiReviewerEnabled")
          : Boolean(currentEvent?.aiReviewerEnabled),
        smtpClearPassword: form.has("smtpClearPassword"),
      };
      body.aiOrganizeEnabled = form.has("aiOrganizeEnabled");
      body.aiJuryEnabled = form.has("aiJuryEnabled");
      body.aiMeetingPlannerEnabled = form.has("aiMeetingPlannerEnabled");
      if (user?.superAdmin) {
        body.aiClearApiKey = form.has("aiClearApiKey");
        body.openAiClearAdminKey = form.has("openAiClearAdminKey");
      } else {
        for (const key of [
          "aiApiKey",
          "aiClearApiKey",
          "openAiAdminKey",
          "openAiClearAdminKey",
          "openAiProjectId",
        ])
          delete body[key];
      }
      for (const image of [
        {
          input: "callForScienceHeroImageFile",
          field: "callForScienceHeroImage",
          remove: "removeCallForScienceHeroImage",
          max: 5_000_000,
          label: "La imagen de fondo de Call for Science",
        },
        {
          input: "whatsappIcon",
          field: "whatsappIconData",
          remove: "removeWhatsappIcon",
          max: 2_000_000,
          label: "El icono de WhatsApp",
        },
        {
          input: "eventBanner",
          field: "bannerData",
          remove: "removeEventBanner",
          max: 5_000_000,
          label: "El banner del evento",
        },
        {
          input: "eventSplash",
          field: "splashData",
          remove: "removeEventSplash",
          max: 5_000_000,
          label: "La imagen de splash",
        },
        {
          input: "emailHeader",
          field: "emailHeaderData",
          remove: "removeEmailHeader",
          max: 5_000_000,
          label: "El encabezado de email",
        },
      ]) {
        const file = form.get(image.input);
        delete body[image.input];
        delete body[image.remove];
        if (file instanceof File && file.size) {
          if (file.size > image.max)
            throw new Error(
              `${image.label} no puede superar los ${image.max / 1_000_000} MB`,
            );
          if (!/image\/(jpeg|png|webp)/.test(file.type))
            throw new Error(`${image.label} debe ser JPG, PNG o WebP`);
          body[image.field] = await fileToDataUrl(file);
        } else if (form.has(image.remove)) body[image.field] = "";
      }
      await api(`/api/events/${eventId}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
      const { events: eventList } = await api<{ events: Event[] }>(
        "/api/events",
      );
      setEvents(eventList);
      showToast("Configuración del evento guardada");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  }

  async function saveCommunicationSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    const form = new FormData(event.currentTarget);
    const currentEvent = events.find((item) => item.id === eventId);
    if (!currentEvent) { setSaving(false); return; }
    const values = Object.fromEntries(form.entries());
    const body: Record<string, unknown> = {
      ...eventSettingsValues(currentEvent),
      ...values,
      subevents: currentEvent.subevents || [],
      organizeEnabled: Boolean(currentEvent.organizeEnabled),
      mobileEnabled: Boolean(currentEvent.mobileEnabled),
      abstractsEnabled: Boolean(currentEvent.abstractsEnabled),
      meetingLive: Boolean(currentEvent.meetingLive),
      smtpClearPassword: form.has("smtpClearPassword"),
    };
    try {
      if (typeof values.sendTestSettings === "string") body.sendTestSettings = JSON.parse(values.sendTestSettings);
      const testAudio = form.get("translationTestAudio");
      delete body.translationTestAudio;
      body.removeTranslationTestAudio = form.has("removeTranslationTestAudio");
      if (testAudio instanceof File && testAudio.size) {
        if (testAudio.size > 8_000_000) throw new Error("El audio de prueba no puede superar los 8 MB");
        if (!/^audio\/(mpeg|mp3|wav|wave|x-wav|webm|mp4|m4a)$/i.test(testAudio.type) && !/\.(mp3|wav|webm|m4a)$/i.test(testAudio.name)) throw new Error("El audio de prueba debe ser MP3, WAV, WebM o M4A");
        body.translationTestAudioData = await fileToDataUrl(testAudio);
        body.translationTestAudioName = testAudio.name;
        body.removeTranslationTestAudio = false;
      }
      await api(`/api/events/${eventId}`, { method: "PUT", body: JSON.stringify(body) });
      const { events: eventList } = await api<{ events: Event[] }>("/api/events");
      setEvents(eventList);
      showToast("Configuración de comunicaciones guardada");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "No se pudo guardar la configuración de comunicaciones");
    } finally { setSaving(false); }
  }

  async function saveMailAccount(form: FormData) {
    setSaving(true);
    try {
      const values = Object.fromEntries(form.entries());
      await api(`/api/events/${eventId}/mail/account`, {
        method: "POST",
        body: JSON.stringify({ prefix: values.prefix, fromName: values.smtpFromName }),
      });
      const { events: eventList } = await api<{ events: Event[] }>("/api/events");
      setEvents(eventList);
      showToast("Cuenta de correo guardada");
      return true;
    } catch (error) {
      showToast(error instanceof Error ? error.message : "No se pudo guardar la cuenta de correo");
      return false;
    } finally { setSaving(false); }
  }

  if (authLoading || Boolean(user && user.role !== "faculty" && eventsLoading))
    return <Splash />;
  if (!user) return <LoginPage onAuthenticated={handleAuthenticated} />;
  if (eventLinkError) return <main className="content"><p role="alert">{eventLinkError}</p><a href="/">Ver eventos</a><button onClick={logout}>Cerrar sesión</button></main>;
  if (user.role === "faculty")
    return <FacultyPortal user={user} onLogout={logout} />;
  if (!activeEvent)
    return (
      <div className="page-stack content">
        <EventsPage events={[]} activeEventId={0} onSelect={() => {}} onNew={user.role === "admin" ? () => setModal({ type: "event" }) : undefined} onEdit={() => {}} onDelete={() => {}} />
        <IconActionButton label="Cerrar sesión" icon="logout" tone="secondary" onClick={logout} />
        {modal && <EditorModal state={modal} catalogs={catalogs} speakers={speakers} sessions={sessions} saving={saving} currentUser={user} onClose={() => setModal(null)} onSubmit={saveModal} onWhatsappGroup={() => {}} onOpenWhatsapp={() => {}} />}
      </div>
    );

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileNav ? "mobile-open" : ""}`}>
        <BrandLogo className="brand" />
        {activeEvents.length > 1 && <div className="event-switcher">
          <span>Evento activo</span>
          <select
            value={eventId}
            onChange={(event) => setEventId(Number(event.target.value))}
            aria-label="Cambiar evento"
          >
            {events.map((event) => (
              <option value={event.id} key={event.id}>
                {event.shortName}
              </option>
            ))}
          </select>
          <small>
            <i />{" "}
            {activeEvent.organizeEnabled
              ? "Activo en Organize"
              : "Fuera de Organize"}
            {activeEvent.mobileEnabled ? " · Mobile" : ""}
          </small>
        </div>}
        <nav className="main-nav" aria-label="Navegación principal">
          <span className="nav-section-label">Gestión</span>
          {visibleNavigation
            .filter((item) => item.section === "management")
            .map((item) => (
              <NavButton
                {...item}
                active={page === item.page}
                onClick={() => navigate(item.page)}
                badge={
                  item.page === "abstracts"
                    ? dashboard?.metrics.pending_abstracts
                    : undefined
                }
                key={item.page}
              />
            ))}
          <span className="nav-section-label second">Análisis y sistema</span>
          {visibleNavigation
            .filter((item) => item.section === "system")
            .map((item) => (
              <NavButton
                {...item}
                active={page === item.page}
                onClick={() => navigate(item.page)}
                key={item.page}
              />
            ))}
        </nav>
        <div className="sidebar-user">
          <span className="avatar">{initials(user.name)}</span>
          <div>
            <strong>{user.name}</strong>
            <span>
              {user.roleName ||
                (user.role === "admin" ? "Administrador" : user.role)}
            </span>
          </div>
          <button onClick={logout} aria-label="Cerrar sesión">
            ↪
          </button>
        </div>
      </aside>
      {mobileNav && (
        <button
          className="mobile-overlay"
          onClick={() => setMobileNav(false)}
          aria-label="Cerrar menú"
        />
      )}

      <div className="workspace">
        <header className="topbar">
          <div className="topbar-left">
            <button
              className="menu-button"
              onClick={() => setMobileNav(true)}
              aria-label="Abrir menú"
            >
              ☰
            </button>
            <div className="breadcrumb">
              <span>{activeEvent.shortName}</span>
              <b>/</b>
              <strong>
                {navigation.find((item) => item.page === page)?.label}
              </strong>
            </div>
          </div>
          <div className="topbar-actions">
            <OrganizeUtilityControls
              languages={interfaceLanguages}
              language={siteLanguage}
              onLanguageChange={setSiteLanguage}
              theme={theme}
              onThemeChange={setTheme}
              loading={pageTranslation.loading}
            />
            {userCanAccess(user, "schedule") && (
              <button
                className="topbar-search"
                onClick={() => {
                  setGlobalSearchQuery("");
                  setGlobalSearchOpen(true);
                }}
                title="Buscar en todo el evento (⌘/Ctrl + K)"
              >
                <span>⌕</span> Buscar en el evento
              </button>
            )}
            {(userCanAccess(user, "speakers") ||
              userCanAccess(user, "communications")) && (
              <WhatsappInboxButton
                inbox={whatsappInbox}
                open={whatsappInboxOpen}
                onToggle={() => setWhatsappInboxOpen((value) => !value)}
                onClose={() => setWhatsappInboxOpen(false)}
                onOpenChat={setWhatsappChatSpeakerId}
              />
            )}
            {activeEvents.length > 1 && (
              <select
                className="topbar-event-select"
                value={eventId}
                onChange={(event) => setEventId(Number(event.target.value))}
                aria-label="Evento activo"
              >
                {activeEvents.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.shortName}
                  </option>
                ))}
              </select>
            )}
            <span className="topbar-user-name">{user.name}</span>
            <span className="topbar-avatar">{initials(user.name)}</span>
          </div>
        </header>
        {activeEvent.bannerData && (
          <div className="organize-event-banner">
            <img
              src={activeEvent.bannerData}
              alt={`Banner de ${activeEvent.name}`}
            />
          </div>
        )}
        <main className="content">
          {page === "dashboard" && (
            <DashboardPage
              event={activeEvent}
              data={loadedEventId === eventId ? dashboard : null}
              days={catalogs.days}
              loading={loading || loadedEventId !== eventId}
              onNewSession={() => setModal({ type: "session" })}
              onNavigate={navigate}
            />
          )}
          {page === "schedule" && (
            <SchedulePage
              eventId={eventId}
              sessions={sessions}
              days={catalogs.days}
              blocks={catalogs.blocks || []}
              rooms={catalogs.rooms}
              loading={loading}
              onNew={(defaults) => setModal({ type: "session", defaults })}
              onEdit={(item) => setModal({ type: "session", item })}
              onAudio={setAudioSession}
              onWhatsappGroup={(item) => setWhatsappGroupSession(item)}
              onWhatsappGroupChat={(item) => setWhatsappGroupChatSession(item)}
              onManageBlocks={() => navigate("settings")}
            />
          )}
          {page === "speakers" && (
            <SpeakersPage
              key={activeEvent.id}
              speakers={speakers}
              sessions={sessions}
              communications={communications}
              eventId={eventId}
              loading={loading}
              onNew={() => setModal({ type: "speaker" })}
              onEdit={(item) => setModal({ type: "speaker", item })}
              onSendCommunication={sendCommunicationToSpeaker}
              onOpenWhatsapp={(speaker) => setWhatsappChatSpeakerId(speaker.id)}
              onUploadPresentation={uploadPresentation}
              onSendPresentation={sendPresentationByWhatsapp}
              onImported={() => loadEverything(eventId)}
            />
          )}
          {page === "abstracts" && callForScienceEnabled && (
            <AbstractsPage
              key={activeEvent.id}
              event={activeEvent}
              abstracts={abstracts}
              scientificTables={catalogs.scientificTables}
              sessions={sessions}
              loading={loading || loadedEventId !== eventId}
              onNew={() => setModal({ type: "abstract" })}
              onEdit={(item) => setModal({ type: "abstract", item })}
              onDelete={(item) =>
                removeResource("abstracts", item, `el trabajo ${item.code}`)
              }
              onStatus={updateAbstractStatus}
              onPdfAiReview={uploadAndCorrectAbstractPdf}
              onReload={() => loadEverything(eventId)}
            />
          )}
          {page === "mail" && <MailPage key={`${eventId}:${activeEvent.smtpUser}`} event={activeEvent} saving={saving} onSaveAccount={saveMailAccount} />}
          {page === "communications" && (
            <CommunicationsPage
              key={eventId}
              onSaveMailAccount={saveMailAccount}
              event={activeEvent}
              eventId={eventId}
              communications={communications}
              sessions={sessions}
              catalogs={catalogs}
              loading={loading}
              saving={saving}
              superAdmin={Boolean(user.superAdmin)}
              onSaveSettings={saveCommunicationSettings}
              onNew={() => setModal({ type: "communication" })}
              onEdit={(item) => setModal({ type: "communication", item })}
            />
          )}
          {page === "organigram" && (
            <OrganigramPage
              abstractsEnabled={Boolean(activeEvent.abstractsEnabled)}
              eventId={eventId}
              communications={communications}
              subevents={catalogs.subevents}
              facultyTypes={catalogs.facultyTypes}
              eventStatuses={catalogs.eventStatuses}
              onProcessed={() => loadEverything(eventId)}
              onOpenCommunications={
                userCanAccess(user, "communications")
                  ? () => navigate("communications")
                  : undefined
              }
            />
          )}
          {page === "meetings" && (
            <MeetingsPage
              eventId={eventId}
              meetings={meetings}
              speakers={speakers}
              loading={loading}
              saving={saving}
              onCreate={createMeeting}
              onStatus={updateMeetingStatus}
              onDelete={removeMeeting}
            />
          )}
          {page === "events" && (
            <EventsPage
              events={events}
              activeEventId={eventId}
              onSelect={(item) => {
                setEventId(item.id);
                navigate("dashboard");
              }}
              onNew={user.role === "admin" ? () => setModal({ type: "event" }) : undefined}
              onEdit={(item) => setModal({ type: "event", item })}
              onDelete={removeEvent}
            />
          )}
          {page === "organizers" && user.superAdmin && <OrganizersPage />}
          {page === "settings" && (
            <SettingsPage
              key={eventId}
              event={activeEvent}
              catalogs={catalogs}
              saving={saving}
              superAdmin={Boolean(user.superAdmin)}
              onSave={saveEventSettings}
              onAdd={(resource) => setModal({ type: "master", resource })}
              onEdit={(resource, item) =>
                setModal({ type: "master", resource, item })
              }
              onDelete={(resource, item) =>
                removeResource(
                  `catalogs/${resource}`,
                  item,
                  `“${"name" in item ? item.name : "el registro"}”`,
                )
              }
              onEventRefresh={async () => {
                const { events: eventList } = await api<{ events: Event[] }>(
                  "/api/events",
                );
                setEvents(eventList);
                await loadEverything(eventId);
              }}
            />
          )}
          {page === "reports" && (
            <ReportsPage
              abstractsEnabled={callForScienceEnabled}
              data={reports}
              eventId={eventId}
              languages={
                activeEvent.aiOrganizeAvailable
                  ? activeEvent.languages
                  : primaryLanguage
              }
              speakers={speakers}
              onReassigned={() => loadEverything(eventId)}
              onToast={showToast}
            />
          )}
          {page === "locations" && <LiveLocationsPage eventId={eventId} />}
          {page === "users" && <UsersPage currentUser={user} events={events} eventId={eventId} />}
        </main>
        {activeEvent.mainSponsorOrganizeFooter ? <MainSponsorFooter fixed sponsor={{
          name: activeEvent.mainSponsorName,
          slogan: activeEvent.mainSponsorSlogan,
          logoUrl: activeEvent.mainSponsorLogoUrl,
          bannerUrl: activeEvent.mainSponsorBannerUrl,
          banners: activeEvent.mainSponsorBanners,
          source: "event",
        }} /> : null}
      </div>

      {modal && (
        <EditorModal
          state={modal}
          catalogs={catalogs}
          speakers={speakers}
          sessions={sessions}
          saving={saving}
          currentUser={user}
          onClose={() => setModal(null)}
          onSubmit={saveModal}
          onDeleteSpeaker={(speaker) => removeResource("speakers", speaker, `${speaker.firstName} ${speaker.lastName}`)}
          onEnrichSpeaker={enrichSpeakerWithAI}
          aiAvailable={activeEvent.aiOrganizeAvailable}
          aiLoadingSpeakerId={aiLoadingSpeakerId}
          onDeleteSession={removeSession}
          onWhatsappGroup={(session) => {
            setModal(null);
            setWhatsappGroupSession(session);
          }}
          onOpenWhatsapp={setWhatsappChatSpeakerId}
        />
      )}
      {audioSession && (
        <SessionAudioModal
          session={audioSession}
          onClose={() => setAudioSession(null)}
        />
      )}
      {aiProfile && (
        <AiProfileModal
          profile={aiProfile}
          onClose={() => setAiProfile(null)}
          onApplyPhoto={applyAiPhoto}
        />
      )}
      {whatsappChatSpeakerId && (
        <WhatsappChatModal
          eventId={eventId}
          speakerId={whatsappChatSpeakerId}
          refreshToken={whatsappRevision}
          realtimeConnected={whatsappRealtimeConnected}
          onClose={() => setWhatsappChatSpeakerId(null)}
          onInboxChange={loadWhatsappInbox}
          onToast={showToast}
        />
      )}
      {whatsappGroupSession && (
        <SessionWhatsappGroupModal
          eventId={eventId}
          session={whatsappGroupSession}
          onClose={() => setWhatsappGroupSession(null)}
          onChanged={() => loadEverything(eventId)}
          onToast={showToast}
        />
      )}
      {whatsappGroupChatSession && (
        <SessionWhatsappGroupChatModal
          eventId={eventId}
          session={whatsappGroupChatSession}
          refreshToken={whatsappRevision}
          realtimeConnected={whatsappRealtimeConnected}
          onClose={() => setWhatsappGroupChatSession(null)}
          onToast={showToast}
        />
      )}
      {globalSearchOpen && (
        <Modal
          title="Buscar en el evento"
          eyebrow={activeEvent.name}
          wide
          className="global-search-modal"
          onClose={() => setGlobalSearchOpen(false)}
        >
          <div className="global-search">
            <label className="global-search-input">
              <span aria-hidden="true">⌕</span>
              <input
                autoFocus
                value={globalSearchQuery}
                onChange={(event) => setGlobalSearchQuery(event.target.value)}
                placeholder="Sesiones, faculty, trabajos, comunicaciones, expositores…"
                aria-label="Texto para buscar"
              />
              <kbd>⌘ K</kbd>
            </label>
            <div className="global-search-results">
              {globalSearchQuery.trim().length < 2 ? (
                <div className="global-search-empty">
                  Ingresá al menos dos caracteres para buscar en todo el evento.
                </div>
              ) : globalSearchResults.length ? (
                globalSearchResults.map((result) => (
                  <button
                    type="button"
                    onClick={() => openGlobalSearchResult(result)}
                    key={result.key}
                  >
                    <span className="global-search-category">
                      {result.category}
                    </span>
                    <span>
                      <strong>{result.title}</strong>
                      <small>{result.detail}</small>
                    </span>
                    <b aria-hidden="true">→</b>
                  </button>
                ))
              ) : (
                <div className="global-search-empty">
                  No se encontraron resultados para “{globalSearchQuery}”.
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <IconActionButton label="Cerrar búsqueda" icon="close" onClick={() => setGlobalSearchOpen(false)} />
          </div>
        </Modal>
      )}
      {toast && (
        <div className="toast" role="status">
          <span>✓</span>
          {toast}
        </div>
      )}
    </div>
  );
}

function NavButton({
  page,
  label,
  active,
  onClick,
  badge,
}: {
  page: NavPage;
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: number;
}) {
  return (
    <button
      className={`nav-button ${active ? "active" : ""}`}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
    >
      <span className="nav-icon" aria-hidden="true">
        <MenuIcon page={page} />
      </span>
      <span>{label}</span>
      {Boolean(badge) && <b>{badge}</b>}
    </button>
  );
}

function MenuIcon({ page }: { page: NavPage }) {
  const common = {
    viewBox: "0 0 24 24",
    width: 18,
    height: 18,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  if (page === "dashboard")
    return (
      <svg {...common}>
        <path d="M3.5 10.5 12 3.7l8.5 6.8" />
        <path d="M5.5 9.3v10.2h13V9.3M9.5 19.5v-6h5v6" />
      </svg>
    );
  if (page === "organigram")
    return (
      <svg {...common}>
        <rect x="8.5" y="3" width="7" height="5" rx="1.2" />
        <rect x="2.5" y="16" width="7" height="5" rx="1.2" />
        <rect x="14.5" y="16" width="7" height="5" rx="1.2" />
        <path d="M12 8v4M6 16v-4h12v4" />
      </svg>
    );
  if (page === "schedule")
    return (
      <svg {...common}>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M7 3v4M17 3v4M3 10h18M7 14h3M14 14h3M7 18h3" />
      </svg>
    );
  if (page === "speakers")
    return (
      <svg {...common}>
        <circle cx="9" cy="8" r="3" />
        <path d="M3.5 20v-1.7A4.3 4.3 0 0 1 7.8 14h2.4a4.3 4.3 0 0 1 4.3 4.3V20M15.5 5.4a3 3 0 0 1 0 5.2M16.5 14.2a4.3 4.3 0 0 1 4 4.3V20" />
      </svg>
    );
  if (page === "abstracts")
    return (
      <svg {...common}>
        <path d="M6 3h8l4 4v14H6zM14 3v5h4M9 12h6M9 16h6" />
      </svg>
    );
  if (page === "meetings")
    return (
      <svg {...common}>
        <rect x="3" y="5" width="13" height="14" rx="2" />
        <path d="m16 10 5-3v10l-5-3zM7 9h5M7 13h4" />
      </svg>
    );
  if (page === "events")
    return (
      <svg {...common}>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M7 3v4M17 3v4M3 10h18M12 13l1 2 2.2.3-1.6 1.6.4 2.1-2-1-2 1 .4-2.1-1.6-1.6L11 15z" />
      </svg>
    );
  if (page === "settings")
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.6v-.2h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1z" />
      </svg>
    );
  if (page === "users")
    return (
      <svg {...common}>
        <circle cx="9" cy="8" r="3" />
        <path d="M3.5 20v-1.5A4.5 4.5 0 0 1 8 14h2a4.5 4.5 0 0 1 3.7 2M18 14v7M14.5 17.5h7" />
      </svg>
    );
  if (page === "mail") return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 6 9 7 9-7" /></svg>;
  if (page === "communications")
    return (
      <svg {...common}>
        <path d="M4 13V7l16-3v12l-16-3zM4 9H2.5v2H4M7 14l1.5 6h3L10 14" />
      </svg>
    );
  return (
    <svg {...common}>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
      <path d="M3 7.5 9 2l6 7 6-5" />
    </svg>
  );
}

function EditorModal({
  state,
  catalogs,
  speakers,
  sessions,
  saving,
  currentUser,
  onClose,
  onSubmit,
  onWhatsappGroup,
  onOpenWhatsapp,
  onDeleteSession,
  onDeleteSpeaker, onEnrichSpeaker, aiAvailable, aiLoadingSpeakerId,
}: {
  state: NonNullable<ModalState>;
  catalogs: Catalogs;
  speakers: Speaker[];
  sessions: Session[];
  saving: boolean;
  currentUser: User;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onWhatsappGroup: (session: Session) => void;
  onOpenWhatsapp: (speakerId: number) => void;
  onDeleteSession?: (session: Session) => void;
  onDeleteSpeaker?: (speaker: Speaker) => void;
  onEnrichSpeaker?: (speakerId: number) => Promise<void>;
  aiAvailable?: boolean;
  aiLoadingSpeakerId?: number | null;
}) {
  const editing = Boolean(state.item);
  const masterNames: Record<CatalogResource, string> = {
    days: "día",
    blocks: "bloque horario",
    rooms: "sala",
    topics: "temática",
    modes: "modalidad",
    roles: "rol",
    exhibitors: "expositor",
    facultyTypes: "tipo de Faculty",
    eventStatuses: "estado del evento",
    scientificTables: "mesa científica",
  };
  const title =
    state.type === "master"
      ? `${editing ? "Editar" : "Agregar"} ${masterNames[state.resource]}`
      : (
          {
            session: editing ? "Editar sesión" : "Nueva sesión",
            speaker: editing ? "Editar persona" : "Agregar al faculty",
            abstract: editing ? "Editar trabajo" : "Nuevo trabajo científico",
            communication: editing
              ? "Editar comunicación"
              : "Nueva comunicación",
            event: editing ? "Editar evento" : "Nuevo evento",
          } as const
        )[state.type];
  return (
    <Modal
      title={title}
      eyebrow={editing ? "Edición" : "Nuevo registro"}
      onClose={onClose}
      wide={
        state.type === "speaker" ||
        state.type === "session" ||
        state.type === "event"
      }
    >
      <form onSubmit={onSubmit}>
        {state.type === "session" && (
          <SessionForm
            item={state.item}
            defaults={state.defaults}
            catalogs={catalogs}
            speakers={speakers}
            onOpenWhatsapp={onOpenWhatsapp}
          />
        )}
        {state.type === "speaker" && (
          <SpeakerForm item={speakers.find(speaker => speaker.id === state.item?.id) || state.item} catalogs={catalogs} onEnrich={state.item && onEnrichSpeaker ? () => onEnrichSpeaker(state.item!.id) : undefined} aiBusy={Boolean(state.item && aiLoadingSpeakerId === state.item.id)} aiEnabled={Boolean(aiAvailable)} />
        )}
        {state.type === "abstract" && (
          <AbstractForm item={state.item} catalogs={catalogs} />
        )}
        {state.type === "communication" && (
          <CommunicationForm
            item={state.item}
            catalogs={catalogs}
            sessions={sessions}
          />
        )}
        {state.type === "event" && <EventForm item={state.item} exhibitors={state.item ? (state.item.sponsorOptions || catalogs.exhibitors) : []} currentUser={currentUser} />}
        {state.type === "master" && (
          <MasterForm
            resource={state.resource}
            item={state.item}
            catalogs={catalogs}
          />
        )}
        {state.type === "session" && (state.item?.sponsorName || state.item?.sponsorBanners?.length) && (
          <MainSponsorFooter
            context="Sponsor de la sesión"
            sponsor={{
              name: state.item.sponsorName,
              slogan: state.item.sponsorSlogan,
              logoUrl: state.item.sponsorLogoUrl,
              bannerUrl: state.item.sponsorBannerUrl,
              banners: state.item.sponsorBanners,
              source: state.item.sponsorSource,
            }}
          />
        )}
        <div className="modal-footer">
          {state.type === "session" && state.item && (
            <div className="modal-leading-actions">
              <WhatsappActionButton
                className="session-whatsapp-group-action"
                label={state.item.whatsappGroupStatus === "active" ? "Administrar grupo de WhatsApp" : "Crear grupo de WhatsApp"}
                onClick={() => onWhatsappGroup(state.item!)}
              />
            </div>
          )}
          {state.type === "speaker" && state.item && onDeleteSpeaker && <IconActionButton label="Eliminar participante" icon="delete" tone="danger" disabled={saving || aiLoadingSpeakerId === state.item.id} onClick={() => onDeleteSpeaker(state.item!)} />}
          {state.type === "session" && state.item && onDeleteSession && <IconActionButton label="Eliminar sesión" icon="delete" tone="danger" disabled={saving} onClick={() => onDeleteSession(state.item!)} />}
          <IconActionButton label="Cancelar" icon="cancel" onClick={onClose} />
          <IconActionButton label={editing ? "Guardar cambios" : "Crear registro"} icon={editing ? "save" : "add"} tone="primary" type="submit" busy={saving} disabled={saving} />
        </div>
      </form>
    </Modal>
  );
}

function SessionAudioModal({
  session,
  onClose,
}: {
  session: Session;
  onClose: () => void;
}) {
  return (
    <Modal
      title={session.title}
      eyebrow="Audio y traducción simultánea"
      onClose={onClose}
      wide
      className="session-audio-modal"
    >
      <LiveTranslationCapture session={session} />
      <div className="modal-footer"><IconActionButton label="Cerrar" icon="close" onClick={onClose} /></div>
    </Modal>
  );
}

function AiProfileModal({
  profile,
  onClose,
  onApplyPhoto,
}: {
  profile: AiSpeakerProfile;
  onClose: () => void;
  onApplyPhoto: () => void;
}) {
  const confidenceLabel =
    profile.confidence === "high"
      ? "Coincidencia alta"
      : profile.confidence === "medium"
        ? "Coincidencia media"
        : "Coincidencia baja";
  return (
    <Modal
      title="Perfil sugerido por IA"
      eyebrow="IA + fuentes publicas"
      wide
      onClose={onClose}
    >
      <div className="ai-profile-result">
        <div className={`ai-confidence confidence-${profile.confidence}`}>
          <strong>{confidenceLabel}</strong>
          <span>{profile.matchExplanation}</span>
        </div>
        {profile.photoUrl ? (
          <section className="ai-photo-suggestion">
            <img
              src={profile.photoUrl}
              alt="Foto profesional sugerida por IA"
            />
            <div>
              <span className="eyebrow">Foto sugerida</span>
              <h3>Revisá la identidad antes de aplicarla</h3>
              <p>La imagen no reemplaza la foto actual hasta que confirmes.</p>
              {profile.photoSourceUrl && (
                <a
                  href={profile.photoSourceUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Abrir página de origen ↗
                </a>
              )}
            </div>
          </section>
        ) : (
          <div className="ai-no-photo">
            La IA no encontró una foto pública suficientemente verificable.
          </div>
        )}
        <div className="ai-summary-grid">
          <article>
            <span className="eyebrow">Perfil profesional</span>
            <p>
              {profile.professionalSummary ||
                "Sin información profesional verificable."}
            </p>
          </article>
          <article>
            <span className="eyebrow">Formación académica</span>
            <p>
              {profile.academicSummary ||
                "Sin información académica verificable."}
            </p>
          </article>
        </div>
        <section className="ai-sources">
          <h3>Fuentes consultadas</h3>
          {profile.sources.length ? (
            <ul>
              {profile.sources.map((source) => (
                <li key={source.url}>
                  <a href={source.url} target="_blank" rel="noreferrer">
                    {source.title || source.url}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p>No se obtuvieron fuentes públicas suficientes.</p>
          )}
        </section>
        <small className="ai-disclaimer">
          Contenido generado con IA a partir de información pública. Verificá
          los datos antes de publicarlos o utilizarlos.
        </small>
      </div>
      <div className="modal-footer">
        <IconActionButton label="Cerrar" icon="close" onClick={onClose} />
        {profile.photoUrl && (
          <IconActionButton label="Usar esta foto" icon="check" tone="primary" onClick={onApplyPhoto} />
        )}
      </div>
    </Modal>
  );
}

function EventForm({ item, exhibitors: initialExhibitors, currentUser }: { item?: Event; exhibitors: Exhibitor[]; currentUser: User }) {
  const [exhibitors, setExhibitors] = useState<Exhibitor[]>(initialExhibitors);
  const [exhibitorsError, setExhibitorsError] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (!item?.id) return;
    api<Catalogs>(`/api/catalogs?${eventQuery(item.id)}`)
      .then(data => { if (!cancelled) { setExhibitors(data.exhibitors); setExhibitorsError(""); } })
      .catch(error => { if (!cancelled) setExhibitorsError(error.message || "No se pudieron cargar los expositores"); });
    return () => { cancelled = true; };
  }, [item?.id]);
  const today = new Date().toISOString().slice(0, 10);
  const [organizers, setOrganizers] = useState<Organizer[]>([]);
  const [organizersError, setOrganizersError] = useState("");
  const [programPublication, setProgramPublication] = useState(item?.mobileProgramPublication || "hidden");
  const [worksPublished, setWorksPublished] = useState(item ? Boolean(item.mobileScientificWorksPublished) : false);
  const [mainSponsorId, setMainSponsorId] = useState(String(item?.mainSponsorExhibitorId || ""));
  useEffect(() => {
    if (!currentUser.superAdmin) return;
    void api<{ items: Organizer[] }>("/api/organizers")
      .then((result) => setOrganizers(result.items))
      .catch((error) => setOrganizersError(error instanceof Error ? error.message : "No se pudieron cargar los organizadores"));
  }, [currentUser.superAdmin]);
  return (
    <div className="form-grid">
      {exhibitorsError && <div className="inline-notice field-wide" role="alert">{exhibitorsError}</div>}
      <label className="field field-wide">
        <span>Nombre del evento *</span>
        <input
          name="name"
          required
          defaultValue={item?.name}
          placeholder="Ej. Congreso Médico 2027"
        />
      </label>
      <label className="field field-wide">
        <span>Organizador *</span>
        {currentUser.superAdmin ? <select name="adminEntityId" required defaultValue={item?.adminEntityId || ""}>
          <option value="" disabled>Seleccionar organizador…</option>
          {organizers.filter((organizer) => organizer.active || organizer.id === item?.adminEntityId).map((organizer) => <option value={organizer.id} key={organizer.id}>{organizer.name}{organizer.active ? "" : " (inactivo)"}</option>)}
        </select> : <input readOnly value={item?.adminEntityName || currentUser.organizerName || ""} />}
        {organizersError && <small className="field-error">{organizersError}</small>}
        <small>Vincula este evento con los anteriores de la misma organización y permite reutilizar su Faculty.</small>
      </label>
      <label className="field">
        <span>Nombre corto *</span>
        <input
          name="shortName"
          required
          defaultValue={item?.shortName}
          placeholder="CM 2027"
        />
      </label>
      {item?.id && item.organizeAccessUuid && <label className="field field-wide"><span>Acceso directo de Organize</span><input readOnly value={`https://organize.virtualmedica.com/${item.id}/${item.organizeAccessUuid}`} onFocus={(event) => event.currentTarget.select()} /><small>Abre el acceso a este evento. Cada usuario ingresa con su cuenta y sus permisos habituales.</small></label>}
      {item?.id && item.callForScienceAccessUuid && <label className="field field-wide">
        <span>Enlace privado de Call for Science</span>
        <input
          readOnly
          value={`https://call4science.virtualmedica.com/${item.id}/${item.callForScienceAccessUuid}`}
          onFocus={(event) => event.currentTarget.select()}
        />
        <small>Compartí este enlace con los participantes. La UUID evita que puedan descubrir otros eventos modificando solamente el número.</small>
      </label>}
      <label className="field field-wide">
        <span>Subdominio personalizado de Call for Science</span>
        <div className="input-with-suffix">
          <input name="callForScienceSubdomain" defaultValue={item?.callForScienceSubdomain || ""} placeholder="solaci2027" autoCapitalize="none" spellCheck={false} />
          <span>.virtualmedica.com</span>
        </div>
        <small>{item?.callForScienceSubdomain ? `Acceso: https://${item.callForScienceSubdomain}.virtualmedica.com` : "Opcional. Usá letras minúsculas, números o guiones; debe ser único entre todos los eventos."}</small>
      </label>
      <label className="field">
        <span>Etapa general</span>
        <select name="status" defaultValue={item?.status || "draft"}>
          <option value="draft">Borrador</option>
          <option value="active">En curso</option>
          <option value="closed">Cerrado</option>
        </select>
      </label>
      <label className="field">
        <span>Fecha de inicio *</span>
        <input
          name="startDate"
          type="date"
          required
          defaultValue={item?.startDate || today}
        />
      </label>
      <label className="field">
        <span>Fecha de cierre *</span>
        <input
          name="endDate"
          type="date"
          required
          defaultValue={item?.endDate || today}
        />
      </label>
      <label className="field">
        <span>Sede</span>
        <input name="venue" defaultValue={item?.venue} />
      </label>
      <label className="field">
        <span>Ciudad</span>
        <input name="city" defaultValue={item?.city} />
      </label>
      <label className="field">
        <span>Idiomas</span>
        <input
          name="languages"
          defaultValue={item?.languages || "es"}
          placeholder="es,en,pt"
        />
      </label>
      <label className="field">
        <span>Color principal</span>
        <input
          className="standalone-color"
          name="primaryColor"
          type="color"
          defaultValue={item?.primaryColor || "#0b918d"}
        />
      </label>
      <label className="field field-wide">
        <span>Main sponsor del evento</span>
        <select name="mainSponsorExhibitorId" value={mainSponsorId} onChange={event => setMainSponsorId(event.target.value)}>
          <option value="">Sin main sponsor</option>
          {exhibitors.filter((exhibitor) => exhibitor.status === "active").map((exhibitor) => (
            <option value={exhibitor.id} key={exhibitor.id}>{exhibitor.name}</option>
          ))}
        </select>
        <small>El logo y banner se toman de la ficha del expositor seleccionado.</small>
      </label>
      <fieldset className="field-wide sponsor-dependent-settings" hidden={!mainSponsorId} disabled={!mainSponsorId}>
      <SponsorSlideEditor name="mainSponsorSlides" initial={item?.mainSponsorSlides?.length ? item.mainSponsorSlides : item?.mainSponsorExhibitorId ? [{exhibitorId:item.mainSponsorExhibitorId,durationSeconds:item.mainSponsorDurationSeconds || 10}] : []} exhibitors={exhibitors} label="Banner" />
      <fieldset className="field field-wide sponsor-placement-settings">
        <legend>Apariciones del main sponsor</legend>
        <div className="sponsor-toggle-grid">
          {[
            ["mainSponsorAppEntry", "Ingreso de la app", item?.mainSponsorAppEntry],
            ["mainSponsorPdfProgram", "Programa PDF", item?.mainSponsorPdfProgram],
            ["mainSponsorProgramFooter", "Pie de cada página del programa", item?.mainSponsorProgramFooter],
            ["mainSponsorConciergeFooter", "Pie del concierge", item?.mainSponsorConciergeFooter],
            ["mainSponsorOrganizeFooter", "Organize", item?.mainSponsorOrganizeFooter],
            ["mainSponsorCall4ScienceFooter", "Call 4 Science", item?.mainSponsorCall4ScienceFooter],
          ].map(([name, label, enabled]) => (
            <label className="sponsor-toggle" key={String(name)}>
              <input name={String(name)} value="1" type="checkbox" role="switch" defaultChecked={item ? Boolean(enabled) : true} />
              <span className="sponsor-toggle-track" aria-hidden="true" />
              <span>{String(label)}</span>
            </label>
          ))}
        </div>
        <div className="form-grid sponsor-placement-controls">
          <label className="field"><span>Duración de la aparición</span><input name="mainSponsorDurationSeconds" type="number" min="1" max="120" defaultValue={item?.mainSponsorDurationSeconds || 10} /><small>Segundos antes del fade out.</small></label>
          <label className="field"><span>Posición en pantalla</span><select name="mainSponsorPosition" defaultValue={item?.mainSponsorPosition || "bottom"}><option value="top">Parte superior</option><option value="bottom">Parte inferior</option></select></label>
        </div>
      </fieldset>
      </fieldset>
      <input type="hidden" name="communicationLanguageMode" value={item?.communicationLanguageMode || "primary"} />
      <fieldset className="field field-wide mobile-publication-settings">
        <legend>Publicación en meetingPlanner</legend>
        <MobileTemplatePicker value={item?.mobileTemplate || "template1"}/>
        {currentUser.superAdmin && <MobileSelectorTemplateSettings/>}
        <div className="form-grid">
          <label className="field">
            <span>Programa disponible en la app</span>
            <select name="mobileProgramPublication" value={programPublication} onChange={(event) => setProgramPublication(event.target.value as Event["mobileProgramPublication"])}>
              <option value="hidden">No publicar programa</option>
              <option value="preliminary">Publicar programa preliminar</option>
              <option value="final">Publicar programa definitivo</option>
            </select>
          </label>
          <label className="publication-toggle">
            <input name="mobileShowParticipantNames" value="1" type="checkbox" defaultChecked={item ? Boolean(item.mobileShowParticipantNames) : true} />
            <span><strong>Detallar nombres de participantes</strong><small>Si se desactiva, se muestran las funciones y presentaciones del programa sin identificar a las personas.</small></span>
          </label>
        </div>
        <div className="publication-divider" />
        <label className="publication-toggle publication-master-toggle">
          <input name="mobileScientificWorksPublished" value="1" type="checkbox" checked={worksPublished} onChange={(event) => setWorksPublished(event.target.checked)} />
          <span><strong>Publicar trabajos científicos</strong><small>El título, los autores y los centros siempre se muestran cuando esta opción está habilitada.</small></span>
        </label>
        <div className="publication-permissions">
          <label className="publication-toggle"><input name="mobileScientificShowAbstract" value="1" type="checkbox" disabled={!worksPublished} defaultChecked={item ? Boolean(item.mobileScientificShowAbstract) : false} /><span><strong>Permitir ver el abstract</strong></span></label>
          <label className="publication-toggle"><input name="mobileScientificShowPresentation" value="1" type="checkbox" disabled={!worksPublished} defaultChecked={item ? Boolean(item.mobileScientificShowPresentation) : false} /><span><strong>Permitir ver la presentación PPT</strong></span></label>
          <label className="publication-toggle"><input name="mobileScientificAllowPdfDownload" value="1" type="checkbox" disabled={!worksPublished} defaultChecked={item ? Boolean(item.mobileScientificAllowPdfDownload) : false} /><span><strong>Permitir visualizar y descargar el PDF</strong></span></label>
        </div>
      </fieldset>
<div className="field field-wide checkbox-row">
        <label>
          <input
            name="organizeEnabled"
            value="1"
            type="checkbox"
            defaultChecked={item ? Boolean(item.organizeEnabled) : true}
          />{" "}
          Activo en Organize
        </label>
        <label>
          <input
            name="mobileEnabled"
            value="1"
            type="checkbox"
            defaultChecked={Boolean(item?.mobileEnabled)}
          />{" "}
          Publicado en Mobile
        </label>
        <label>
          <input
            name="abstractsEnabled"
            value="1"
            type="checkbox"
            defaultChecked={item ? Boolean(item.abstractsEnabled) : true}
          />{" "}
          Habilitar Call for Science
        </label>
        <label>
          <input
            name="meetingLive"
            value="1"
            type="checkbox"
            defaultChecked={Boolean(item?.meetingLive)}
          />{" "}
          Habilitar Meeting Live
        </label>
      </div>
      <input type="hidden" name="emailFooter" value={item?.emailFooter || ""} />
      <EventStructureEditor
        initial={item?.subevents || []}
        eventId={item?.id}
        exhibitors={exhibitors}
      />
    </div>
  );
}

function EventStructureEditor({
  initial,
  eventId,
  exhibitors,
}: {
  initial: EventSubevent[];
  eventId?: number;
  exhibitors: Exhibitor[];
}) {
  const [subevents, setSubevents] = useState<EventSubevent[]>(initial);
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [inviteMessage, setInviteMessage] = useState<Record<number, string>>(
    {},
  );
  const update = (index: number, changes: Partial<EventSubevent>) =>
    setSubevents((items) =>
      items.map((item, position) =>
        position === index ? { ...item, ...changes } : item,
      ),
    );
  const updateSigner = (
    subeventIndex: number,
    signerIndex: number,
    field: "name" | "function",
    value: string,
  ) =>
    setSubevents((items) =>
      items.map((item, position) =>
        position !== subeventIndex
          ? item
          : {
              ...item,
              signers: item.signers.map((signer, signerPosition) =>
                signerPosition === signerIndex
                  ? { ...signer, [field]: value }
                  : signer,
              ),
            },
      ),
    );
  async function sendInvitations(subevent: EventSubevent) {
    if (!eventId || !subevent.id) return;
    setSendingId(subevent.id);
    try {
      const result = await api<{ testMode?: boolean; faculty: number; participations: number }>(
        `/api/subevents/${subevent.id}/send-participation-invitations`,
        { method: "POST", body: JSON.stringify({ eventId }) },
      );
      if (result.testMode) { setInviteMessage(messages => ({...messages,[subevent.id!]: "Prueba procesada. Las invitaciones reales siguen pendientes."})); return; }
      setInviteMessage((messages) => ({
        ...messages,
        [subevent.id!]: `Envío procesado: ${result.faculty} faculty · ${result.participations} participaciones pendientes.`,
      }));
      update(
        subevents.findIndex((item) => item.id === subevent.id),
        { programStatus: "faculty_review" },
      );
    } catch (error) {
      if ((error as {status?: number})?.status === 499) return ;
      setInviteMessage((messages) => ({
        ...messages,
        [subevent.id!]:
          error instanceof Error
            ? error.message
            : "No se pudieron emitir las invitaciones",
      }));
    } finally {
      setSendingId(null);
    }
  }
  return (
    <section className="event-subevents field-wide">
      <input type="hidden" name="subevents" value={JSON.stringify(subevents)} />
      <details className="event-subevents-collapse">
        <summary>
          <div>
            <span className="eyebrow">Estructura del evento</span>
            <h3>Subeventos, estados y firmantes</h3>
            <p>
              Cada subevento administra su propia etapa, invitaciones y
              disponibilidad de materiales.
            </p>
          </div>
          <div className="subevent-collapse-state">
            <span>
              {subevents.length}{" "}
              {subevents.length === 1 ? "subevento" : "subeventos"}
            </span>
            <b>⌄</b>
          </div>
        </summary>
        <div className="subevent-collapse-body">
          <header>
            <span>Configuración de subeventos</span>
            <IconActionButton
              label="Agregar subevento"
              icon="add"
              onClick={() =>
                setSubevents((items) => [
                  ...items,
                  {
                    title: "",
                    sortOrder: items.length + 1,
                    programStatus: "planning",
                    mainSponsorExhibitorId: null,
                    signers: [],
                  },
                ])
              }
            />
          </header>
          {!subevents.length ? (
            <div className="subevent-empty">
              Sin subeventos. Las sesiones aparecerán dentro de “Programa
              general”.
            </div>
          ) : (
            subevents.map((subevent, index) => (
              <article
                className="subevent-editor"
                key={subevent.id || `new-${index}`}
              >
                <div className="subevent-main">
                  <label className="field subevent-title">
                    <span>Título *</span>
                    <input
                      required
                      value={subevent.title}
                      onChange={(event) =>
                        update(index, { title: event.target.value })
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Estado del programa</span>
                    <select
                      value={subevent.programStatus || "planning"}
                      onChange={(event) =>
                        update(index, {
                          programStatus: event.target
                            .value as EventSubevent["programStatus"],
                        })
                      }
                    >
                      <option value="planning">En planificación</option>
                      <option value="preliminary_complete">
                        Programa preliminar finalizado
                      </option>
                      <option value="faculty_review">
                        Respuestas del faculty
                      </option>
                      <option value="final">Programa final</option>
                    </select>
                  </label>
                  <label className="field">
                    <span>Main sponsor del subevento</span>
                    <select
                      value={subevent.mainSponsorExhibitorId || ""}
                      onChange={(event) => update(index, { mainSponsorExhibitorId: event.target.value ? Number(event.target.value) : null })}
                    >
                      <option value="">Heredar main sponsor del evento</option>
                      {exhibitors.filter((exhibitor) => exhibitor.status === "active").map((exhibitor) => (
                        <option value={exhibitor.id} key={exhibitor.id}>{exhibitor.name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="field subevent-order">
                    <span>Orden</span>
                    <input
                      type="number"
                      min="0"
                      value={subevent.sortOrder}
                      onChange={(event) =>
                        update(index, { sortOrder: Number(event.target.value) })
                      }
                    />
                  </label>
                  <button
                    type="button"
                    className="participation-remove"
                    onClick={() =>
                      setSubevents((items) =>
                        items.filter((_, position) => position !== index),
                      )
                    }
                  >
                    ×
                  </button>
                </div>
                <div className="subevent-signers">
                  <strong>Firmantes de comunicaciones</strong>
                  {subevent.signers.map((signer, signerIndex) => (
                    <div key={signer.id || `signer-${signerIndex}`}>
                      <input
                        aria-label="Nombre del firmante"
                        placeholder="Nombre y apellido"
                        value={signer.name}
                        onChange={(event) =>
                          updateSigner(
                            index,
                            signerIndex,
                            "name",
                            event.target.value,
                          )
                        }
                      />
                      <input
                        aria-label="Función del firmante"
                        placeholder="Función / cargo"
                        value={signer.function}
                        onChange={(event) =>
                          updateSigner(
                            index,
                            signerIndex,
                            "function",
                            event.target.value,
                          )
                        }
                      />
                      <button
                        type="button"
                        onClick={() =>
                          update(index, {
                            signers: subevent.signers.filter(
                              (_, position) => position !== signerIndex,
                            ),
                          })
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="subevent-add-signer"
                    onClick={() =>
                      update(index, {
                        signers: [
                          ...subevent.signers,
                          {
                            name: "",
                            function: "",
                            sortOrder: subevent.signers.length + 1,
                          },
                        ],
                      })
                    }
                  >
                    + Agregar firmante
                  </button>
                </div>
                {subevent.id &&
                  ["preliminary_complete", "faculty_review"].includes(
                    subevent.programStatus,
                  ) && (
                    <div className="subevent-invitations">
                      <IconActionButton
                        label="Emitir invitaciones pendientes"
                        icon="send"
                        tone="primary"
                        busy={sendingId === subevent.id}
                        disabled={sendingId === subevent.id}
                        onClick={() => sendInvitations(subevent)}
                      />
                      <small>
                        Modelo protegido. Cada reenvío incluye solamente
                        participaciones todavía sin respuesta.
                      </small>
                      {inviteMessage[subevent.id] && (
                        <p>{inviteMessage[subevent.id]}</p>
                      )}
                    </div>
                  )}
              </article>
            ))
          )}
        </div>
      </details>
    </section>
  );
}

function MasterForm({
  resource,
  item,
  catalogs,
}: {
  resource: CatalogResource;
  item?: CatalogItem;
  catalogs: Catalogs;
}) {
  const named = item && "name" in item ? item : undefined;
  if (resource === "days") {
    const value = item && "date" in item ? item : undefined;
    return (
      <div className="form-grid">
        <label className="field field-wide">
          <span>Denominación *</span>
          <input
            name="name"
            required
            defaultValue={value?.name}
            placeholder="Ej. Jueves 22"
          />
        </label>
        <label className="field">
          <span>Fecha *</span>
          <input name="date" type="date" required defaultValue={value?.date} />
        </label>
        <label className="field">
          <span>Orden</span>
          <input
            name="sortOrder"
            type="number"
            min="0"
            defaultValue={value?.sortOrder || 0}
          />
        </label>
        <label className="check-field field-wide">
          <input
            name="isStart"
            value="1"
            type="checkbox"
            defaultChecked={Boolean(value?.isStart)}
          />
          <span>Es la jornada inicial del evento</span>
        </label>
      </div>
    );
  }
  if (resource === "blocks") {
    const value = item && "startTime" in item ? item : undefined;
    return (
      <div className="form-grid">
        <label className="field field-wide">
          <span>Jornada *</span>
          <select
            name="dayId"
            required
            defaultValue={value?.dayId || catalogs.days[0]?.id}
          >
            <option value="" disabled>
              Seleccionar jornada
            </option>
            {catalogs.days.map((day) => (
              <option value={day.id} key={day.id}>
                {day.name} · {day.date}
              </option>
            ))}
          </select>
        </label>
        <label className="field field-wide">
          <span>Nombre del bloque *</span>
          <input
            name="name"
            required
            defaultValue={value?.name}
            placeholder="Ej. Primera franja"
          />
        </label>
        <label className="field">
          <span>Hora de inicio *</span>
          <input
            name="startTime"
            type="time"
            required
            defaultValue={value?.startTime || "09:00"}
          />
        </label>
        <label className="field">
          <span>Hora de fin *</span>
          <input
            name="endTime"
            type="time"
            required
            defaultValue={value?.endTime || "10:00"}
          />
        </label>
        <label className="field">
          <span>Orden</span>
          <input
            name="sortOrder"
            type="number"
            min="0"
            defaultValue={value?.sortOrder || 0}
          />
        </label>
        {!catalogs.days.length && (
          <p className="form-hint field-wide">
            Primero tenés que crear al menos un día del evento.
          </p>
        )}
      </div>
    );
  }
  if (resource === "rooms") {
    const value = item && "streamingCode" in item ? item : undefined;
    return (
      <div className="form-grid">
        <label className="field field-wide">
          <span>Nombre de la sala *</span>
          <input name="name" required defaultValue={value?.name} />
        </label>
        <label className="field">
          <span>Orden</span>
          <input
            name="sortOrder"
            type="number"
            min="0"
            defaultValue={value?.sortOrder || 0}
          />
        </label>
        <label className="field">
          <span>Código de streaming</span>
          <input name="streamingCode" defaultValue={value?.streamingCode} />
        </label>
        <label className="field field-wide">
          <span>URL de transmisión</span>
          <input
            name="liveUrl"
            type="url"
            defaultValue={value?.liveUrl}
            placeholder="https://…"
          />
        </label>
      <BeaconEditor key={`room-${value?.id || 'new'}`} title="Balizas de esta sala" plans={catalogs.floorPlans || []} initial={catalogs.beacons.filter(beacon => value && (beacon.roomId === value.id || beacon.id === value.beaconId))} />
      </div>
    );
  }
  if (resource === "topics") {
    const value = item && "application" in item ? item : undefined;
    return (
      <div className="form-grid">
        <label className="field field-wide">
          <span>Nombre de la temática *</span>
          <input name="name" required defaultValue={value?.name} />
        </label>
        <label className="field">
          <span>Aplicación</span>
          <select
            name="application"
            defaultValue={value?.application || "program"}
          >
            <option value="program">Programa científico</option>
            <option value="abstracts">Trabajos científicos</option>
            <option value="both">Ambos</option>
          </select>
        </label>
        <label className="field">
          <span>Color</span>
          <input
            className="standalone-color"
            name="color"
            type="color"
            defaultValue={value?.color || "#0b918d"}
          />
        </label>
      </div>
    );
  }
  if (resource === "modes") {
    const value = item && "abstractsLinked" in item ? item : undefined;
    return (
      <div className="form-grid">
        <label className="field field-wide">
          <span>Nombre de la modalidad *</span>
          <input name="name" required defaultValue={value?.name} />
        </label>
        <label className="field">
          <span>Prefijo</span>
          <input
            name="prefix"
            defaultValue={value?.prefix}
            placeholder="CONF"
          />
        </label>
        <label className="field">
          <span>Color</span>
          <input
            className="standalone-color"
            name="color"
            type="color"
            defaultValue={value?.color || "#dceeed"}
          />
        </label>
        <label className="field">
          <span>Minutos previos</span>
          <input
            name="participationPre"
            type="number"
            min="0"
            defaultValue={value?.participationPre || 0}
          />
        </label>
        <label className="field">
          <span>Minutos posteriores</span>
          <input
            name="participationPost"
            type="number"
            min="0"
            defaultValue={value?.participationPost || 0}
          />
        </label>
        <label className="check-field field-wide">
          <input
            name="abstractsLinked"
            value="1"
            type="checkbox"
            defaultChecked={Boolean(value?.abstractsLinked)}
          />
          <span>Permite vincular trabajos científicos</span>
        </label>
      </div>
    );
  }
  if (resource === "roles") {
    const value = item && "participationMinutes" in item ? item : undefined;
    return (
      <div className="form-grid">
        <label className="field">
          <span>Nombre del rol *</span>
          <input name="name" required defaultValue={value?.name} />
        </label>
        <label className="field">
          <span>Nombre plural</span>
          <input name="pluralName" defaultValue={value?.pluralName} />
        </label>
        <label className="field">
          <span>Duración predeterminada</span>
          <input
            name="participationMinutes"
            type="number"
            min="0"
            defaultValue={value?.participationMinutes || 15}
          />
        </label>
        <div className="field checkbox-row">
          <label>
            <input
              name="showInProgram"
              value="1"
              type="checkbox"
              defaultChecked={value ? Boolean(value.showInProgram) : true}
            />{" "}
            Mostrar en programa
          </label>
          <label>
            <input
              name="fullAccess"
              value="1"
              type="checkbox"
              defaultChecked={Boolean(value?.fullAccess)}
            />{" "}
            Acceso completo
          </label>
        </div>
      </div>
    );
  }
  if (resource === "facultyTypes") {
    const value =
      item && "description" in item && "active" in item ? item : undefined;
    return (
      <div className="form-grid">
        <label className="field field-wide">
          <span>Nombre del tipo *</span>
          <input
            name="name"
            required
            defaultValue={value?.name || named?.name}
            placeholder="Ej. Latinoamericanos"
          />
        </label>
        <label className="field field-wide">
          <span>Descripción</span>
          <textarea
            name="description"
            rows={3}
            defaultValue={value?.description}
            placeholder="Criterio de clasificación o alcance"
          />
        </label>
        <label className="field">
          <span>Orden</span>
          <input
            name="sortOrder"
            type="number"
            min="0"
            defaultValue={value?.sortOrder || 0}
          />
        </label>
        <label className="check-field">
          <input
            name="active"
            value="1"
            type="checkbox"
            defaultChecked={value ? Boolean(value.active) : true}
          />
          <span>Disponible para nuevas asignaciones</span>
        </label>
      </div>
    );
  }
  if (resource === "eventStatuses") {
    const value =
      item && "description" in item && "color" in item && "active" in item
        ? item
        : undefined;
    return (
      <div className="form-grid">
        <label className="field">
          <span>Área *</span>
          <select name="scope" defaultValue={value?.scope || "organize"}>
            <option value="organize">Organize</option>
            <option value="abstracts">Abstracts</option>
          </select>
          <small>Los dos circuitos son independientes.</small>
        </label>
        <label className="field">
          <span>Nombre del estado *</span>
          <input
            name="name"
            required
            defaultValue={value?.name || named?.name}
            placeholder="Ej. Programa preliminar finalizado"
          />
        </label>
        <label className="field field-wide">
          <span>Descripción</span>
          <textarea
            name="description"
            rows={3}
            defaultValue={value?.description}
            placeholder="Qué representa este estado dentro de su circuito"
          />
        </label>
        <label className="field">
          <span>Color</span>
          <input
            className="standalone-color"
            name="color"
            type="color"
            defaultValue={value?.color || "#487da8"}
          />
        </label>
        <label className="field">
          <span>Orden</span>
          <input
            name="sortOrder"
            type="number"
            min="0"
            defaultValue={value?.sortOrder || 0}
          />
        </label>
        <label className="check-field field-wide">
          <input
            name="active"
            value="1"
            type="checkbox"
            defaultChecked={value ? Boolean(value.active) : true}
          />
          <span>Disponible para acciones del Organigrama</span>
        </label>
      </div>
    );
  }
  if (resource === "scientificTables") {
    const value = item && "type" in item && "active" in item ? item : undefined;
    const abstractTopics = catalogs.topics.filter(
      (topic) =>
        topic.application === "abstracts" || topic.application === "both",
    );
    return (
      <div className="form-grid">
        <label className="field field-wide">
          <span>Nombre *</span>
          <input
            name="name"
            required
            defaultValue={value?.name}
            placeholder="Ej. Mesa 1 · Cardiología clínica"
          />
        </label>
        <label className="field field-wide">
          <span>Subevento *</span>
          <select name="subeventId" required defaultValue={value?.subeventId || ""}>
            <option value="" disabled>Seleccionar subevento</option>
            {catalogs.subevents.map((subevent) => <option value={subevent.id} key={subevent.id}>{subevent.title}</option>)}
          </select>
          <small>La mesa sólo podrá recibir trabajos presentados en este subevento.</small>
        </label>
        <label className="field">
          <span>Tipo *</span>
          <select name="type" defaultValue={value?.type || "oral"}>
            <option value="oral">Mesa de presentaciones orales</option>
            <option value="poster">Sesión de pósters</option>
          </select>
        </label>
        <label className="field">
          <span>Temática</span>
          <select name="topicId" defaultValue={value?.topicId || ""}>
            <option value="">Todas / sin temática</option>
            {abstractTopics.map((topic) => (
              <option value={topic.id} key={topic.id}>
                {topic.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Presentación por trabajo (minutos)</span>
          <input name="presentationMinutes" type="number" min="0" step="1" defaultValue={value?.presentationMinutes ?? 10} />
        </label>
        <label className="field">
          <span>Discusión por trabajo (minutos)</span>
          <input name="discussionMinutes" type="number" min="0" step="1" defaultValue={value?.discussionMinutes ?? 5} />
        </label>
        <label className="field">
          <span>Orden</span>
          <input
            name="sortOrder"
            type="number"
            min="0"
            defaultValue={value?.sortOrder || 0}
          />
        </label>
        <label className="check-field">
          <input
            name="active"
            value="1"
            type="checkbox"
            defaultChecked={value ? Boolean(value.active) : true}
          />
          <span>Disponible para asignaciones</span>
        </label>
      </div>
    );
  }
  const value = item && "standCode" in item ? item : undefined;
  return (
    <div className="form-grid">
      <label className="field field-wide">
        <span>Nombre del expositor *</span>
        <input name="name" required defaultValue={value?.name || named?.name} />
      </label>
      <label className="field">
        <span>Código de stand</span>
        <input name="standCode" defaultValue={value?.standCode} />
      </label>
      <label className="field">
        <span>Email de contacto</span>
        <input
          name="contactEmail"
          type="email"
          defaultValue={value?.contactEmail}
        />
      </label>
      <label className="field">
        <span>Estado</span>
        <select name="status" defaultValue={value?.status || "active"}>
          <option value="active">Activo</option>
          <option value="inactive">Inactivo</option>
        </select>
      </label>
      <label className="check-field field-wide">
        <input
          name="beaconEmissionEnabled"
          value="1"
          type="checkbox"
          role="switch"
          defaultChecked={value ? Boolean(value.beaconEmissionEnabled) : true}
        />
        <span>
          Permitir emisión de geolocalización
          <small>El personal de esta empresa podrá usar meetingPlanner para convertir un celular en emisor de presencia de sus stands.</small>
        </span>
      </label>
      <BeaconEditor key={`exhibitor-${value?.id || 'new'}`} title="Balizas de los stands" plans={catalogs.floorPlans || []} initial={catalogs.beacons.filter(beacon => value && beacon.targetType !== 'room' && beacon.exhibitorId === value.id)} />
      <label className="field field-wide">
        <span>Slogan</span>
        <input
          name="slogan"
          defaultValue={value?.slogan}
          placeholder="Frase institucional o comercial"
        />
      </label>
      <label className="field field-wide">
        <span>Contenido libre (HTML)</span>
        <textarea
          name="htmlContent"
          rows={9}
          defaultValue={value?.htmlContent || ""}
          placeholder="<p>Descripción, productos, enlaces y datos de contacto…</p>"
        />
        <small>
          Admite HTML. La app muestra texto, listas, negritas, cursivas y
          enlaces.
        </small>
      </label>
      <ExhibitorAssetField
        name="logo"
        title="Logo del expositor"
        current={value?.logoUrl || ""}
        removeName="removeLogo"
      />
      <ExhibitorAssetField
        name="banner"
        title="Banner para comunicaciones"
        current={value?.bannerUrl || ""}
        removeName="removeBanner"
        wide
      />
      <ExhibitorUserAssignments exhibitor={value} users={catalogs.exhibitorUsers || []} />
      <ExhibitorPersonnelFields exhibitor={value} />
    </div>
  );
}

function ExhibitorUserAssignments({ exhibitor, users }: { exhibitor?: Exhibitor; users: Catalogs["exhibitorUsers"] }) {
  return <fieldset className="permission-selector field-wide"><legend>Usuarios expositores de Organize</legend><p className="form-hint">Asigná cuentas que tengan el rol predefinido Expositor. Una cuenta puede pertenecer a una sola empresa.</p>{users.length ? users.map((user) => <label key={user.id}><input name="exhibitorUserIds" type="checkbox" value={user.id} defaultChecked={Number(user.exhibitorId) === Number(exhibitor?.id)} /><span><strong>{user.name}</strong><small>{user.email}{user.exhibitorId && Number(user.exhibitorId) !== Number(exhibitor?.id) ? ` · actualmente en ${user.exhibitorName}` : ""}</small></span></label>) : <p className="form-hint">Primero creá usuarios con rol Expositor desde Usuarios.</p>}</fieldset>;
}

function ExhibitorPersonnelFields({ exhibitor }: { exhibitor?: Exhibitor }) {
  const [staff, setStaff] = useState<ExhibitorStaff[]>(() => exhibitor?.staff?.map((item) => ({ ...item, password: "" })) || []);
  const update = (index: number, changes: Partial<ExhibitorStaff>) => setStaff((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, ...changes } : item));
  const add = () => setStaff((current) => [...current, { name: "", email: "", phone: "", position: "", active: 1, password: "" }]);
  return (
    <div className="field field-wide exhibitor-personnel">
      <input type="hidden" name="staff" value={JSON.stringify(staff)} />
      <div className="exhibitor-personnel-heading">
        <div><strong>Personal con acceso móvil</strong><small>Estas credenciales son independientes de los usuarios de Organize y permiten registrar visitas desde la app.</small></div>
        <IconActionButton label="Agregar persona" icon="add" onClick={add} />
      </div>
      {staff.length ? <div className="exhibitor-personnel-list">{staff.map((person, index) => (
        <div className="exhibitor-person-card" key={person.id || `new-${index}`}>
          <label className="field"><span>Nombre *</span><input required value={person.name} onChange={(event) => update(index, { name: event.target.value })} /></label>
          <label className="field"><span>Email de acceso *</span><input required type="email" value={person.email} onChange={(event) => update(index, { email: event.target.value })} /></label>
          <label className="field"><span>Teléfono</span><input value={person.phone} onChange={(event) => update(index, { phone: event.target.value })} /></label>
          <label className="field"><span>Cargo</span><input value={person.position} placeholder="Ej. Representante comercial" onChange={(event) => update(index, { position: event.target.value })} /></label>
          <label className="field"><span>{person.id ? "Nueva clave (opcional)" : "Clave inicial *"}</span><input required={!person.id} type="password" minLength={6} value={person.password || ""} placeholder={person.passwordConfigured ? "Conservar clave actual" : "Mínimo 6 caracteres"} onChange={(event) => update(index, { password: event.target.value })} /></label>
          <label className="check-field exhibitor-person-active"><input type="checkbox" checked={Boolean(person.active)} onChange={(event) => update(index, { active: event.target.checked ? 1 : 0 })} /><span>Acceso activo</span></label>
          <button type="button" className="icon-button danger exhibitor-person-remove" aria-label="Quitar persona" onClick={() => setStaff((current) => current.filter((_, itemIndex) => itemIndex !== index))}>×</button>
        </div>
      ))}</div> : <p className="form-hint">Todavía no hay personal asociado a este expositor.</p>}
      {exhibitor?.recentVisits?.length ? <div className="exhibitor-visits"><strong>Últimas visitas registradas</strong><div className="exhibitor-visits-table"><div className="exhibitor-visit-row header"><span>Fecha</span><span>Asistente</span><span>Origen</span><span>Registró</span></div>{exhibitor.recentVisits.map((visit) => <div className="exhibitor-visit-row" key={visit.id}><span>{new Date(visit.scannedAt).toLocaleString("es-AR")}</span><span>{visit.visitorName}</span><span>{visit.visitType === "stand" ? `Stand ${exhibitor.standCode || ""}` : visit.sessionTitle}</span><span>{visit.staffName}</span></div>)}</div></div> : null}
    </div>
  );
}

function ExhibitorAssetField({
  name,
  title,
  current,
  removeName,
  wide = false,
}: {
  name: string;
  title: string;
  current: string;
  removeName: string;
  wide?: boolean;
}) {
  const [preview, setPreview] = useState(current);
  return (
    <div className={`exhibitor-asset-field ${wide ? "field-wide" : ""}`}>
      <div className={`exhibitor-asset-preview ${wide ? "banner" : "logo"}`}>
        {preview ? <img src={preview} alt={title} /> : <span>Sin imagen</span>}
      </div>
      <div>
        <strong>{title}</strong>
        <small>JPG, PNG o WebP.</small>
        <label className="action-icon-button secondary" title={`Seleccionar ${title}`} aria-label={`Seleccionar ${title}`}>
          <ActionIcon name="upload" />
          <span className="visually-hidden">Seleccionar {title}</span>
          <input
            name={name}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) setPreview(URL.createObjectURL(file));
            }}
          />
        </label>
        {current && (
          <label className="photo-remove">
            <input
              name={removeName}
              type="checkbox"
              value="1"
              onChange={(event) =>
                event.target.checked ? setPreview("") : setPreview(current)
              }
            />{" "}
            Quitar actual
          </label>
        )}
      </div>
    </div>
  );
}

function SessionForm({
  item,
  defaults,
  catalogs,
  speakers,
  onOpenWhatsapp,
}: {
  item?: Session;
  defaults?: SessionDefaults;
  catalogs: Catalogs;
  speakers: Speaker[];
  onOpenWhatsapp: (speakerId: number) => void;
}) {
  const [activeTab, setActiveTab] = useState<"details" | "faculty">("details");
  const tabId = useId();
  const assignableSpeakers = speakers.filter((speaker) => speaker.status === "confirmed");
  const initialDayId =
    item?.dayId || defaults?.dayId || catalogs.days[0]?.id || 0;
  const [selectedDayId, setSelectedDayId] = useState(initialDayId);
  const [roomIds, setRoomIds] = useState<number[]>(
    item?.roomIds?.length
      ? item.roomIds
      : ([defaults?.roomId || item?.roomId || catalogs.rooms[0]?.id].filter(
          Boolean,
        ) as number[]),
  );
  const [blockIds, setBlockIds] = useState<number[]>(
    item?.blockIds?.length
      ? item.blockIds
      : ([defaults?.blockId].filter(Boolean) as number[]),
  );
  const [scientificTableIds, setScientificTableIds] = useState<number[]>(
    item?.scientificTableIds || [],
  );
  const [selectedSubeventId, setSelectedSubeventId] = useState<number>(item?.subeventId || 0);
  const [participations, setParticipations] = useState<Participation[]>(
    item?.participations || [],
  );
  const [selectedMaterial, setSelectedMaterial] = useState<Participation | null>(null);
  const dayBlocks = catalogs.blocks.filter(
    (block) => block.dayId === selectedDayId,
  );

  function toggle(
    list: number[],
    id: number,
    setter: (value: number[]) => void,
  ) {
    setter(
      list.includes(id) ? list.filter((value) => value !== id) : [...list, id],
    );
  }
  function updateParticipation(index: number, changes: Partial<Participation>) {
    setParticipations((current) =>
      current.map((participation, position) =>
        position === index ? { ...participation, ...changes } : participation,
      ),
    );
  }
  function addParticipation() {
    if (!assignableSpeakers[0]) return;
    setParticipations((current) => [
      ...current,
      {
        speakerId: assignableSpeakers[0].id,
        roleId: catalogs.roles[0]?.id || null,
        title: "",
        durationMinutes: 15,
        sortOrder: current.length + 1,
      },
    ]);
  }

  return (
    <div className="form-grid session-editor-grid" onInvalidCapture={(event) => {
      const target = event.target as HTMLInputElement;
      const panel = target.closest<HTMLElement>("[data-session-panel]");
      if (panel?.hidden) {
        event.preventDefault();
        setActiveTab(panel.dataset.sessionPanel as "details" | "faculty");
        requestAnimationFrame(() => { target.focus(); target.reportValidity(); });
      }
    }}>
      <div className="session-editor-tabs field-wide" role="tablist" aria-label="Edición de la sesión">
        {([['details', 'Datos de la sesión'], ['faculty', 'Participaciones del faculty']] as const).map(([tab, label]) => (
          <button key={tab} type="button" role="tab" id={`${tabId}-${tab}-tab`} aria-controls={`${tabId}-${tab}-panel`} aria-selected={activeTab === tab} tabIndex={activeTab === tab ? 0 : -1} onClick={() => setActiveTab(tab)} onKeyDown={(event) => {
            if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
            event.preventDefault();
            const next = event.key === "Home" ? "details" : event.key === "End" ? "faculty" : tab === "details" ? "faculty" : "details";
            setActiveTab(next);
            document.getElementById(`${tabId}-${next}-tab`)?.focus();
          }}>{label}{tab === "faculty" && <span className="session-tab-count">{participations.length}</span>}</button>
        ))}
      </div>
      <input type="hidden" name="roomIds" value={JSON.stringify(roomIds)} />
      <input type="hidden" name="blockIds" value={JSON.stringify(blockIds)} />
      <input
        type="hidden"
        name="scientificTableIds"
        value={JSON.stringify(scientificTableIds)}
      />
      <input
        type="hidden"
        name="participations"
        value={JSON.stringify(participations)}
      />
      <div className="form-grid session-editor-tab-panel field-wide" role="tabpanel" id={`${tabId}-details-panel`} aria-labelledby={`${tabId}-details-tab`} data-session-panel="details" hidden={activeTab !== "details"}>
      <label className="field field-wide">
        <span>Título de la sesión *</span>
        <input
          name="title"
          required
          defaultValue={item?.title}
          placeholder="Ej. Nuevos desafíos en cardiología clínica"
        />
      </label>
      <label className="field">
        <span>Jornada *</span>
        <select
          value={selectedDayId}
          onChange={(event) => {
            setSelectedDayId(Number(event.target.value));
            setBlockIds([]);
          }}
        >
          {catalogs.days.map((day) => (
            <option value={day.id} key={day.id}>
              {day.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Estado</span>
        <select name="status" defaultValue={item?.status || "draft"}>
          <option value="draft">Borrador</option>
          <option value="confirmed">Confirmada</option>
          <option value="live">En vivo</option>
          <option value="cancelled">Cancelada</option>
        </select>
      </label>
      <label className="field field-wide">
        <span>Subevento</span>
        <select name="subeventId" value={selectedSubeventId || ""} onChange={(event) => {
          const nextSubeventId = Number(event.target.value) || 0;
          setSelectedSubeventId(nextSubeventId);
          setScientificTableIds((current) => current.filter((id) => catalogs.scientificTables.some((table) => table.id === id && table.subeventId === nextSubeventId)));
        }}>
          <option value="">Programa general</option>
          {catalogs.subevents.map((subevent) => (
            <option value={subevent.id} key={subevent.id}>
              {subevent.sortOrder}. {subevent.title}
            </option>
          ))}
        </select>
      </label>
      <fieldset className="assignment-field field-wide">
        <legend>Bloques horarios *</legend>
        <p>Podés seleccionar uno o varios bloques contiguos.</p>
        <div className="assignment-options">
          {dayBlocks.map((block) => (
            <label
              className={blockIds.includes(block.id) ? "selected" : ""}
              key={block.id}
            >
              <input
                type="checkbox"
                checked={blockIds.includes(block.id)}
                onChange={() => toggle(blockIds, block.id, setBlockIds)}
              />
              <strong>
                {block.startTime}–{block.endTime}
              </strong>
              <span>{block.name}</span>
            </label>
          ))}
        </div>
        {!dayBlocks.length && (
          <small>No hay bloques configurados para esta jornada.</small>
        )}
      </fieldset>
      <fieldset className="assignment-field field-wide">
        <legend>Salones *</legend>
        <p>
          Seleccioná uno o varios salones contiguos según el orden configurado.
        </p>
        <div className="assignment-options rooms">
          {catalogs.rooms.map((room) => (
            <label
              className={roomIds.includes(room.id) ? "selected" : ""}
              key={room.id}
            >
              <input
                type="checkbox"
                checked={roomIds.includes(room.id)}
                onChange={() => toggle(roomIds, room.id, setRoomIds)}
              />
              <strong>{room.name}</strong>
              <span>Orden {room.sortOrder}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <label className="field">
        <span>Temática</span>
        <select name="topicId" defaultValue={item?.topicId || ""}>
          <option value="">Sin temática</option>
          {catalogs.topics.map((topic) => (
            <option value={topic.id} key={topic.id}>
              {topic.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Modalidad</span>
        <select name="modeId" defaultValue={item?.modeId || ""}>
          <option value="">Sin modalidad</option>
          {catalogs.modes.map((mode) => (
            <option value={mode.id} key={mode.id}>
              {mode.name}
            </option>
          ))}
        </select>
      </label>
      <fieldset className="assignment-field field-wide">
        <legend>Mesas científicas y sesiones de pósters</legend>
        <p>Una sesión del programa puede contener una o varias mesas.</p>
        <div className="assignment-options">
          {catalogs.scientificTables
            .filter(
              (table) => table.subeventId === selectedSubeventId && (table.active || scientificTableIds.includes(table.id)),
            )
            .map((table) => (
              <label
                className={
                  scientificTableIds.includes(table.id) ? "selected" : ""
                }
                key={table.id}
              >
                <input
                  type="checkbox"
                  checked={scientificTableIds.includes(table.id)}
                  onChange={() =>
                    toggle(scientificTableIds, table.id, setScientificTableIds)
                  }
                />
                <strong>{table.name}</strong>
                <span>
                  {table.type === "oral" ? "Orales" : "Pósters"}
                  {table.topicName ? ` · ${table.topicName}` : ""}
                  {table.subeventTitle ? ` · ${table.subeventTitle}` : ""}
                </span>
              </label>
            ))}
        </div>
        {!selectedSubeventId ? <small>Seleccioná un subevento para ver sus mesas disponibles.</small> : !catalogs.scientificTables.some((table) => table.subeventId === selectedSubeventId) && <small>No hay mesas para este subevento. Crealas desde Ajustes → Mesas científicas.</small>}
      </fieldset>
      <label className="field field-wide">
        <span>Sesión auspiciada por</span>
        <select
          name="sponsorExhibitorId"
          defaultValue={item?.sponsorExhibitorId || ""}
        >
          <option value="">Sin auspiciante</option>
          {catalogs.exhibitors
            .filter((exhibitor) => exhibitor.status === "active")
            .map((exhibitor) => (
              <option value={exhibitor.id} key={exhibitor.id}>
                {exhibitor.name}
                {exhibitor.slogan ? ` · ${exhibitor.slogan}` : ""}
              </option>
            ))}
        </select>
        <small>
          El pie y el banner se incorporarán automáticamente a las
          comunicaciones vinculadas con esta sesión.
        </small>
      </label>
      <div className="session-registration-options field-wide">
        <label className="check-field">
          <input
            name="requiresRegistration"
            value="1"
            type="checkbox"
            defaultChecked={Boolean(item?.requiresRegistration)}
          />
          <span>
            <strong>Requiere Registro Previo</strong>
            <small>
              La persona deberá inscribirse antes de participar de esta sesión.
            </small>
          </span>
        </label>
        <label className="check-field">
          <input
            name="attendanceCheckin"
            value="1"
            type="checkbox"
            defaultChecked={Boolean(item?.attendanceCheckin)}
          />
          <span>
            <strong>Registro de Presencia al Acceso</strong>
            <small>
              Se controlará y registrará el ingreso presencial a la sesión.
            </small>
          </span>
        </label>
      </div>
      <label className="field field-wide">
        <span>URL de transmisión</span>
        <input
          name="streamUrl"
          type="url"
          defaultValue={item?.streamUrl}
          placeholder="https://…"
        />
      </label>
      <label className="field field-wide">
        <span>Objetivos</span>
        <textarea
          name="objectives"
          rows={3}
          defaultValue={item?.objectives}
          placeholder="Objetivos y descripción breve de la sesión"
        />
      </label>
      </div>
      <div className="session-editor-tab-panel field-wide" role="tabpanel" id={`${tabId}-faculty-panel`} aria-labelledby={`${tabId}-faculty-tab`} data-session-panel="faculty" hidden={activeTab !== "faculty"}>
      <section className="participation-editor field-wide">
        <header>
          <div>
            <span className="eyebrow">Faculty de la sesión</span>
            <h3>Participaciones</h3>
            <p>Asigná personas, roles, temas y tiempos de intervención.</p>
          </div>
          <IconActionButton
            label="Agregar participación"
            icon="add"
            onClick={addParticipation}
            disabled={!assignableSpeakers.length}
          />
        </header>
        {!assignableSpeakers.length && <p className="participation-empty">No hay faculty confirmados disponibles. Deben aceptar la invitación o ser marcados como Confirmado desde Faculty.</p>}
        {!participations.length ? (
          <div className="participation-empty">
            Todavía no hay integrantes del faculty asignados.
          </div>
        ) : (
          <div className="participation-list">
            {participations.map((participation, index) => {
              const participant = speakers.find(
                (speaker) => speaker.id === participation.speakerId,
              );
              return (
                <div
                  className="participation-row"
                  key={`${participation.id || "new"}-${index}`}
                >
                  <span className="participation-order">{index + 1}</span>
                  <ParticipationIdentity speaker={participant} />
                  <WhatsappActionButton
                    className="participation-whatsapp-action"
                    label={`Abrir WhatsApp de ${participant ? `${participant.firstName} ${participant.lastName}` : "este participante"}`}
                    disabled={!participant?.phone}
                    onClick={() =>
                      participant && onOpenWhatsapp(participant.id)
                    }
                  />
                  <label>
                    <span>Faculty</span>
                    <select
                      value={participation.speakerId}
                      onChange={(event) =>
                        updateParticipation(index, {
                          speakerId: Number(event.target.value),
                        })
                      }
                    >
                      {(!participant || participant.status !== "confirmed") && <option disabled value={participation.speakerId}>{participant ? `${participant.firstName} ${participant.lastName}` : participation.speakerName || "Faculty"} · {participant ? "Sin confirmar" : "Fuera de Event"}</option>}
                      {assignableSpeakers.map((speaker) => (
                        <option value={speaker.id} key={speaker.id}>
                          {speaker.firstName} {speaker.lastName}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Rol</span>
                    <select
                      value={participation.roleId || ""}
                      onChange={(event) =>
                        updateParticipation(index, {
                          roleId: Number(event.target.value) || null,
                        })
                      }
                    >
                      <option value="">Sin rol</option>
                      {catalogs.roles.map((role) => (
                        <option value={role.id} key={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="participation-title">
                    <span>Título / intervención</span>
                    <input
                      value={participation.title}
                      onChange={(event) =>
                        updateParticipation(index, {
                          title: event.target.value,
                        })
                      }
                      placeholder="Tema de la participación"
                    />
                  </label>
                  <label>
                    <span>Minutos</span>
                    <input
                      type="number"
                      min="0"
                      value={participation.durationMinutes}
                      onChange={(event) =>
                        updateParticipation(index, {
                          durationMinutes: Number(event.target.value),
                        })
                      }
                    />
                  </label>
                  <div className="participation-connection">
                    <label><span>Modalidad de participación</span><select value={participation.attendanceMode || "presential"} onChange={(event) => updateParticipation(index, { attendanceMode: event.target.value as "presential" | "video_meeting" })}><option value="presential">Presencial</option><option value="video_meeting">Video Meeting</option></select></label>
                    {participation.attendanceMode === "video_meeting" && <span>{item?.videoMeetingUrl ? <a href={item.videoMeetingUrl} target="_blank" rel="noreferrer">Abrir sala Video Meeting ↗</a> : "Al guardar se asignará una sala a esta sesión."}<small>El faculty tendrá el acceso en su portal. La traducción simultánea es opcional.</small></span>}
                  </div>
                  <div className="participation-admin-state">
                    <span
                      className={`participation-response ${participation.status || "pending"}`}
                    >
                      {participationStatusLabel(participation.status)}
                    </span>
                    {participation.presentationName &&
                      participation.id &&
                      item?.eventId && (
                        <IconActionButton icon="view" label={`Ver material: ${participation.presentationName}`} onClick={() => setSelectedMaterial(participation)} />
                      )}
                    <ParticipationRecordingButton eventId={item?.eventId} activityId={participation.id} title={participation.title} /><small>{participation.quizCount || 0} quizzes</small>
                  </div>
                  <button
                    type="button"
                    className="participation-remove"
                    onClick={() =>
                      setParticipations((current) =>
                        current.filter((_, position) => position !== index),
                      )
                    }
                    aria-label="Quitar participación"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>
      </div>
      {selectedMaterial?.id && item?.eventId && <MaterialPreviewModal
        title={selectedMaterial.title || "Presentación"}
        eyebrow="Material de la participación"
        fileName={selectedMaterial.presentationName || "presentacion"}
        mimeType={selectedMaterial.presentationMime || ""}
        url={`/api/speakers/${selectedMaterial.speakerId}/participations/${selectedMaterial.id}/presentation?eventId=${item.eventId}`}
        onClose={() => setSelectedMaterial(null)}
      />}
    </div>
  );
}

function participationStatusLabel(status?: string) {
  return status === "accepted"
    ? "Aceptada"
    : status === "rejected"
      ? "Rechazada"
      : "Pendiente";
}

function ParticipationIdentity({ speaker }: { speaker?: Speaker }) {
  if (!speaker) return <span className="participation-identity-placeholder" />;
  return (
    <ParticipantAvatar
      small
      name={`${speaker.firstName} ${speaker.lastName}`}
      country={speaker.country}
      photoUrl={speaker.photoUrl}
    />
  );
}

function SpeakerForm({
  item,
  catalogs, onEnrich, aiBusy, aiEnabled,
}: {
  item?: Speaker;
  catalogs: Catalogs;
  onEnrich?: () => void;
  aiBusy?: boolean;
  aiEnabled?: boolean;
}) {
  const eligibleTopics = catalogs.topics.filter(
    (topic) =>
      topic.application === "abstracts" || topic.application === "both",
  );
  const [isReviewer, setIsReviewer] = useState(Boolean(item?.isReviewer));
  const [reviewTopicIds, setReviewTopicIds] = useState<number[]>(
    item?.reviewTopics?.map((topic) => topic.id) || [],
  );
  const [reviewAllTopics, setReviewAllTopics] = useState(Boolean(item?.reviewAllTopics));
  const [reviewAllSubevents, setReviewAllSubevents] = useState(item ? Boolean(item.reviewAllSubevents) : true);
  const [reviewSubeventIds, setReviewSubeventIds] = useState<number[]>(item?.reviewSubevents?.map((subevent) => subevent.id).filter((id): id is number => Number.isInteger(id)) || []);
  const [contactViaAssistant, setContactViaAssistant] = useState(
    Boolean(item?.contactViaAssistant),
  );
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  function toggleReviewTopic(topicId: number) {
    setReviewTopicIds((current) =>
      current.includes(topicId)
        ? current.filter((id) => id !== topicId)
        : [...current, topicId],
    );
  }

  return (
    <div className="form-grid">
      <input
        type="hidden"
        name="reviewTopicIds"
        value={JSON.stringify(reviewTopicIds)}
      />
      <input type="hidden" name="reviewSubeventIds" value={JSON.stringify(reviewSubeventIds)} />
      <PhotoField item={item} onEnrich={onEnrich} aiBusy={aiBusy} aiEnabled={aiEnabled} />
      <label className="field">
        <span>Tratamiento</span>
        <select name="prefix" defaultValue={item?.prefix || "Dr."}>
          <option>Dr.</option>
          <option>Dra.</option>
          <option>Prof.</option>
          <option>Lic.</option>
        </select>
      </label>
      <input
        type="hidden"
        name="category"
        value={item?.category || "Faculty"}
      />
      <label className="field">
        <span>Tipo de Faculty</span>
        <select name="facultyTypeId" defaultValue={item?.facultyTypeId || ""}>
          <option value="">Sin tipo asignado</option>
          {catalogs.facultyTypes
            .filter((type) => type.active || type.id === item?.facultyTypeId)
            .map((type) => (
              <option value={type.id} key={type.id}>
                {type.name}
              </option>
            ))}
        </select>
        <small>
          Clasificación independiente de sus funciones. Se administra desde
          Ajustes → Tipos de Faculty.
        </small>
      </label>
      <label className="field">
        <span>Nombre *</span>
        <input name="firstName" required defaultValue={item?.firstName} />
      </label>
      <label className="field">
        <span>Apellido *</span>
        <input name="lastName" required defaultValue={item?.lastName} />
      </label>
      <label className="field">
        <span>Email *</span>
        <input name="email" type="email" required defaultValue={item?.email} />
      </label>
      <label className="field">
        <span>Teléfono</span>
        <input name="phone" defaultValue={item?.phone} />
      </label>
      <label className="field">
        <span>País</span>
        <input name="country" defaultValue={item?.country} />
      </label>
      <label className="field">
        <span>Institución</span>
        <input name="institution" defaultValue={item?.institution} />
      </label>
      <label className="check-field field-wide">
        <input
          name="contactViaAssistant"
          value="1"
          type="checkbox"
          checked={contactViaAssistant}
          onChange={(event) => setContactViaAssistant(event.target.checked)}
        />
        <span>
          <strong>Contactarme por mi asistente</strong>
          <small>
            Los emails y WhatsApp directos se enviarán al asistente. Los grupos
            de sesiones conservarán el WhatsApp personal del faculty.
          </small>
        </span>
      </label>
      <label className="field">
        <span>Nombre del asistente{contactViaAssistant ? " *" : ""}</span>
        <input
          name="assistantName"
          required={contactViaAssistant}
          defaultValue={item?.assistantName}
        />
      </label>
      <label className="field">
        <span>Teléfono del asistente{contactViaAssistant ? " *" : ""}</span>
        <input
          name="assistantPhone"
          required={contactViaAssistant}
          defaultValue={item?.assistantPhone}
        />
      </label>
      <label className="field">
        <span>Email del asistente{contactViaAssistant ? " *" : ""}</span>
        <input
          name="assistantEmail"
          type="email"
          required={contactViaAssistant}
          defaultValue={item?.assistantEmail}
        />
      </label>
      <label className="field">
        <span>Estado</span>
        <select name="status" defaultValue={item?.status || "invited"}>
          <option value="invited">Invitado</option>
          <option value="confirmed">Confirmado</option>
          <option value="declined">No participa</option>
        </select>
      </label>
      <label className="field">
        <span>Confirmación</span>
        <select
          name="confirmationStatus"
          defaultValue={item?.confirmationStatus || "pending"}
        >
          <option value="pending">Pendiente</option>
          <option value="confirmed">Confirmada</option>
          <option value="declined">Rechazada</option>
        </select>
      </label>
      <label className="field">
        <span>Acceso</span>
        <select
          name="accessStatus"
          defaultValue={item?.accessStatus || "pending"}
        >
          <option value="pending">Pendiente</option>
          <option value="active">Habilitado</option>
          <option value="blocked">Bloqueado</option>
        </select>
      </label>
      <div className="field field-wide">
        <span>Clave de acceso</span>
        <small>
          {item?.passwordConfigured
            ? "Este faculty ya tiene una clave. Completá ambos campos solamente si querés reemplazarla."
            : "Este faculty todavía no tiene una clave. Completá ambos campos para habilitar el acceso por email y contraseña."}
        </small>
      </div>
      <label className="field">
        <span>{item?.passwordConfigured ? "Nueva clave" : "Crear clave"}</span>
        <span className="password-input-wrap">
          <input
            name="password"
            type={showPassword ? "text" : "password"}
            value={password}
            minLength={6}
            maxLength={128}
            autoComplete="new-password"
            placeholder="Mínimo 6 caracteres"
            onChange={(event) => setPassword(event.target.value)}
          />
          <button
            type="button"
            onClick={() => setShowPassword((current) => !current)}
            aria-label={showPassword ? "Ocultar clave" : "Mostrar clave"}
            aria-pressed={showPassword}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"/><circle cx="12" cy="12" r="2.8"/></svg>
          </button>
        </span>
      </label>
      <label className="field">
        <span>Confirmar nueva clave</span>
        <span className="password-input-wrap">
          <input
            name="passwordConfirmation"
            type={showPassword ? "text" : "password"}
            value={passwordConfirmation}
            minLength={6}
            maxLength={128}
            autoComplete="new-password"
            required={Boolean(password)}
            placeholder="Repetí la nueva clave"
            onChange={(event) => setPasswordConfirmation(event.target.value)}
          />
          <button
            type="button"
            onClick={() => setShowPassword((current) => !current)}
            aria-label={showPassword ? "Ocultar confirmación" : "Mostrar confirmación"}
            aria-pressed={showPassword}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"/><circle cx="12" cy="12" r="2.8"/></svg>
          </button>
        </span>
      </label>
      <label className="check-field field-wide reviewer-toggle">
        <input
          name="isReviewer"
          value="1"
          type="checkbox"
          checked={isReviewer}
          onChange={(event) => {
            setIsReviewer(event.target.checked);
            if (!event.target.checked) setReviewTopicIds([]);
          }}
        />
        <span>
          <strong>Es árbitro de trabajos científicos</strong>
          <small>
            Permite asignarle las unidades temáticas que podrá evaluar.
          </small>
        </span>
      </label>
      {isReviewer && (
        <><fieldset className="assignment-field reviewer-topic-field field-wide">
          <legend>Unidades temáticas de arbitraje</legend>
          <p>
            Solo se muestran las temáticas configuradas para Trabajos
            científicos o Ambos.
          </p>
          <label className="check-field reviewer-all-option"><input name="reviewAllTopics" value="1" type="checkbox" checked={reviewAllTopics} onChange={(event) => { setReviewAllTopics(event.target.checked); if (event.target.checked) setReviewTopicIds([]); }} /><span><strong>Todas las unidades temáticas</strong></span></label>
          {!reviewAllTopics && eligibleTopics.length ? (
            <div className="assignment-options reviewer-topics">
              {eligibleTopics.map((topic) => (
                <label
                  className={
                    reviewTopicIds.includes(topic.id) ? "selected" : ""
                  }
                  key={topic.id}
                >
                  <input
                    type="checkbox"
                    checked={reviewTopicIds.includes(topic.id)}
                    onChange={() => toggleReviewTopic(topic.id)}
                  />
                  <i style={{ background: topic.color }} />
                  <strong>{topic.name}</strong>
                  <span>
                    {topic.application === "both"
                      ? "Programa y trabajos"
                      : "Trabajos científicos"}
                  </span>
                </label>
              ))}
            </div>
          ) : !reviewAllTopics ? (
            <small>
              No hay temáticas habilitadas para trabajos científicos. Podés
              configurarlas desde Ajustes → Temáticas.
            </small>
          ) : null}
        </fieldset>
        <fieldset className="assignment-field reviewer-topic-field field-wide">
          <legend>Subeventos que puede corregir</legend>
          <label className="check-field reviewer-all-option"><input name="reviewAllSubevents" value="1" type="checkbox" checked={reviewAllSubevents} onChange={(event) => { setReviewAllSubevents(event.target.checked); if (event.target.checked) setReviewSubeventIds([]); }} /><span><strong>Todos los subeventos</strong></span></label>
          {!reviewAllSubevents && <div className="assignment-options reviewer-topics">{catalogs.subevents.filter((subevent): subevent is typeof subevent & { id: number } => Number.isInteger(subevent.id)).map((subevent) => <label className={reviewSubeventIds.includes(subevent.id) ? "selected" : ""} key={subevent.id}><input type="checkbox" checked={reviewSubeventIds.includes(subevent.id)} onChange={() => setReviewSubeventIds((current) => current.includes(subevent.id) ? current.filter((id) => id !== subevent.id) : [...current, subevent.id])} /><strong>{subevent.title}</strong></label>)}</div>}
        </fieldset></>
      )}
      <label className="field field-wide">
        <span>CV / reseña</span>
        <textarea name="cv" rows={4} defaultValue={item?.cv} />
      </label>
      <label className="field field-wide">
        <span>Resumen profesional</span>
        <textarea
          key={item?.professionalSummary}
          name="professionalSummary"
          rows={4}
          defaultValue={item?.professionalSummary}
          placeholder="Puede completarse manualmente o mediante IA"
        />
      </label>
      <label className="field field-wide">
        <span>Resumen académico</span>
        <textarea
          key={item?.academicSummary}
          name="academicSummary"
          rows={4}
          defaultValue={item?.academicSummary}
          placeholder="Puede completarse manualmente o mediante IA"
        />
      </label>
    </div>
  );
}

function PhotoField({ item, onEnrich, aiBusy, aiEnabled }: { item?: Speaker; onEnrich?: () => void; aiBusy?: boolean; aiEnabled?: boolean }) {
  const [preview, setPreview] = useState(item?.photoUrl || "");
  useEffect(() => setPreview(item?.photoUrl || ""), [item?.photoUrl]);
  return (
    <div className={`photo-field field-wide ${onEnrich ? "with-ai-action" : ""}`}>
      {onEnrich && <button type="button" className="ai-action photo-ai-action" title={aiEnabled ? "Recuperar foto y perfil con IA" : "IA no disponible para este evento. Revisá su habilitación y configuración en Ajustes."} aria-label={aiEnabled ? "Recuperar foto y perfil con IA" : "IA no disponible para este evento"} disabled={aiBusy || !aiEnabled} onClick={onEnrich}>{aiBusy ? "…" : <AiIcon />}</button>}
      <input type="hidden" name="photoUrl" value={item?.photoUrl || ""} />
      <div className="photo-preview">
        {preview ? (
          <img
            src={preview}
            alt={`Foto de ${item?.firstName || "participante"}`}
          />
        ) : (
          <span>
            {item ? initials(`${item.firstName} ${item.lastName}`) : "FO"}
          </span>
        )}
      </div>
      <div>
        <strong>Foto del participante</strong>
        <p>
          Se utilizará en el programa y en todas sus participaciones. JPG, PNG o
          WebP, hasta 2 MB.
        </p>
        <label className="action-icon-button secondary" title="Seleccionar foto" aria-label="Seleccionar foto">
          <ActionIcon name="upload" />
          <span className="visually-hidden">Seleccionar foto</span>
          <input
            name="photo"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) setPreview(URL.createObjectURL(file));
            }}
          />
        </label>
        {item?.photoUrl && (
          <label className="photo-remove">
            <input
              type="checkbox"
              name="removePhoto"
              value="1"
              onChange={(event) => event.target.checked && setPreview("")}
            />{" "}
            Quitar foto actual
          </label>
        )}
      </div>
    </div>
  );
}

function AbstractForm({
  item,
  catalogs,
}: {
  item?: Abstract;
  catalogs: Catalogs;
}) {
  const abstractTopics = catalogs.topics.filter(
    (topic) =>
      topic.application === "abstracts" || topic.application === "both",
  );
  return (
    <div className="form-grid">
      <label className="field">
        <span>ID del trabajo *</span>
        <input
          name="code"
          required
          defaultValue={item?.code}
          placeholder="A-007"
        />
      </label>
      <label className="field">
        <span>Formato preferido</span>
        <select
          name="preferredFormat"
          defaultValue={item?.preferredFormat || "oral"}
        >
          <option value="oral">Oral</option>
          <option value="poster">Póster</option>
        </select>
      </label>
      <label className="field field-wide">
        <span>Título *</span>
        <textarea name="title" rows={3} required defaultValue={item?.title} />
      </label>
      <label className="field">
        <span>Responsable *</span>
        <input
          name="submitterName"
          required
          defaultValue={item?.submitterName}
        />
      </label>
      <label className="field">
        <span>Email *</span>
        <input
          name="submitterEmail"
          type="email"
          required
          defaultValue={item?.submitterEmail}
        />
      </label>
      <label className="field">
        <span>País</span>
        <input name="country" defaultValue={item?.country} />
      </label>
      <label className="field">
        <span>Unidad temática</span>
        <select name="topicId" defaultValue={item?.topicId || ""}>
          <option value="">Sin temática asignada</option>
          {abstractTopics.map((topic) => (
            <option value={topic.id} key={topic.id}>
              {topic.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Mesa / sesión de pósters</span>
        <select
          name="scientificTableId"
          defaultValue={item?.scientificTableId || ""}
        >
          <option value="">Sin mesa asignada</option>
          {catalogs.scientificTables
            .filter(
              (table) => table.active || table.id === item?.scientificTableId,
            )
            .map((table) => (
              <option value={table.id} key={table.id}>
                {table.type === "oral" ? "Oral" : "Póster"} · {table.name}
              </option>
            ))}
        </select>
      </label>
      <label className="field">
        <span>Categoría</span>
        <input name="category" defaultValue={item?.category} />
      </label>
      <label className="field field-wide">
        <span>Resumen del trabajo</span>
        <textarea
          name="abstractText"
          rows={8}
          defaultValue={item?.abstractText}
          placeholder="Texto que estará disponible para el arbitraje humano y por IA"
        />
      </label>
      <label className="field">
        <span>Estado</span>
        <select name="status" defaultValue={item?.status || "pending"}>
          <option value="pending">Pendiente</option>
          <option value="in_review">En evaluación</option>
          <option value="accepted">Aceptado</option>
          <option value="rejected">Rechazado</option>
        </select>
      </label>
      <label className="field">
        <span>Puntaje</span>
        <input
          name="score"
          type="number"
          min="0"
          max="10"
          step="0.1"
          defaultValue={item?.score ?? ""}
        />
      </label>
    </div>
  );
}

function CommunicationForm({
  item,
  catalogs,
  sessions,
}: {
  item?: Communication;
  catalogs: Catalogs;
  sessions: Session[];
}) {
  const [channels, setChannels] = useState<CommunicationChannel[]>(
    item?.channels?.length ? item.channels : [item?.channel || "email"],
  );
  const toggleChannel = (channel: CommunicationChannel, checked: boolean) =>
    setChannels((current) => {
      if (checked)
        return current.includes(channel) ? current : [...current, channel];
      return current.length > 1
        ? current.filter((value) => value !== channel)
        : current;
    });
  return (
    <div className="form-grid">
      {item?.protected ? (
        <div className="inline-notice field-wide">
          Plantilla protegida: podés modificar sus textos y configuración, pero no eliminarla.
        </div>
      ) : null}
      <fieldset className="channel-selector field-wide">
        <legend>Canales de envío</legend>
        <p>Seleccioná uno o varios; se intentarán enviar al mismo tiempo.</p>
        <div>
          {(["email", "whatsapp", "push"] as CommunicationChannel[]).map(
            (channel) => {
              const selected = channels.includes(channel);
              return (
              <button
                type="button"
                className={`channel-choice channel-${channel} ${channels.includes(channel) ? "selected" : ""}`}
                key={channel}
                aria-pressed={selected}
                onClick={() => toggleChannel(channel, !selected)}
              >
                <span className="channel-symbol">
                  {channel === "email" ? (
                    "@"
                  ) : channel === "whatsapp" ? (
                    <WhatsappIcon />
                  ) : (
                    "↗"
                  )}
                </span>
                <strong>{communicationChannelLabel(channel)}</strong>
                <span className="channel-toggle" aria-hidden="true"><span /></span>
              </button>
              );
            },
          )}
          {channels.map((channel) => <input key={channel} type="hidden" name="channels" value={channel} />)}
        </div>
      </fieldset>
      <label className="field">
        <span>Tipo *</span>
        <input
          name="type"
          required
          defaultValue={item?.type}
          placeholder="Ej. Recordatorio"
        />
      </label>
      <label className="field">
        <span>Subevento y firmantes</span>
        <select name="subeventId" defaultValue={item?.subeventId || ""}>
          <option value="">Sin subevento / sin firmantes</option>
          {catalogs.subevents.map((subevent) => (
            <option value={subevent.id} key={subevent.id}>
              {subevent.title}
            </option>
          ))}
        </select>
      </label>
      <label className="field field-wide">
        <span>Sesión relacionada</span>
        <select name="sessionId" defaultValue={item?.sessionId || ""}>
          <option value="">Comunicación general</option>
          {sessions.map((session) => (
            <option value={session.id} key={session.id}>
              {session.dayName} · {session.startTime} · {session.title}
              {session.sponsorName
                ? ` · Auspiciada por ${session.sponsorName}`
                : ""}
            </option>
          ))}
        </select>
        <small>
          Si la sesión tiene un auspiciante asignado directamente, se agregará
          automáticamente a email y WhatsApp.
        </small>
      </label>
      <label className="field field-wide">
        <span>Asunto *</span>
        <input name="subject" required defaultValue={item?.subject} />
      </label>
      <label className="field">
        <span>Audiencia</span>
        <select name="audience" defaultValue={item?.audience || "all"}>
          <option value="all">Todos</option>
          <option value="invited">Faculty invitado</option>
          <option value="pending_confirmation">
            Pendientes de confirmación
          </option>
          <option value="confirmed">Confirmados</option>
          <option value="abstract_submitters">Autores de trabajos</option>
        </select>
      </label>
      <label className="field">
        <span>Estado</span>
        <select name="status" defaultValue={item?.status || "draft"}>
          <option value="draft">Borrador</option>
          <option value="scheduled">Programado</option>
          <option value="sent">Enviado</option>
        </select>
      </label>
      {channels.includes("email") ? (
        <WysiwygEditor
          initialHtml={(item?.emailHtml || plainTextToHtml(item?.body || "")) + (item?.systemCode === "invite_participation" && !/\{(?:aceptar|rechazar)_invitacion\}/.test((item?.emailHtml || "") + (item?.body || "")) ? '<p><a href="{aceptar_invitacion}">Aceptar invitación</a></p><p><a href="{rechazar_invitacion}">No participar</a></p>' : "")}
          simultaneous={channels.length > 1}
        />
      ) : (
        <label className="field field-wide">
          <span>Mensaje *</span>
          <textarea name="body" rows={7} required defaultValue={item?.body} />
        </label>
      )}
    </div>
  );
}

function WysiwygEditor({
  initialHtml,
  simultaneous,
}: {
  initialHtml: string;
  simultaneous: boolean;
}) {
  const editor = useRef<HTMLDivElement>(null);
  const htmlInput = useRef<HTMLInputElement>(null);
  const plainInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const safeHtml = sanitizeRichHtml(initialHtml);
    if (editor.current) editor.current.innerHTML = safeHtml;
    if (htmlInput.current) htmlInput.current.value = safeHtml;
    if (plainInput.current) plainInput.current.value = htmlToPlainText(safeHtml);
  }, [initialHtml]);
  const sync = () => {
    const nextHtml = sanitizeRichHtml(editor.current?.innerHTML || "");
    if (htmlInput.current) htmlInput.current.value = nextHtml;
    if (plainInput.current) plainInput.current.value = htmlToPlainText(nextHtml);
  };
  const command = (name: string, value?: string) => {
    editor.current?.focus();
    document.execCommand(name, false, value);
    sync();
  };
  return (
    <div className="field field-wide wysiwyg-field">
      <span>Mensaje de email *</span>
      <input ref={htmlInput} type="hidden" name="emailHtml" defaultValue={initialHtml} />
      <input ref={plainInput} type="hidden" name="body" defaultValue={htmlToPlainText(initialHtml)} />
      <div
        className="wysiwyg-toolbar"
        role="toolbar"
        aria-label="Formato del email"
      >
        <button
          type="button"
          onMouseDown={(event) => {
            event.preventDefault();
            command("bold");
          }}
        >
          <strong>N</strong>
        </button>
        <button
          type="button"
          onMouseDown={(event) => {
            event.preventDefault();
            command("italic");
          }}
        >
          <em>C</em>
        </button>
        <button
          type="button"
          onMouseDown={(event) => {
            event.preventDefault();
            command("underline");
          }}
        >
          <u>S</u>
        </button>
        <button
          type="button"
          onMouseDown={(event) => {
            event.preventDefault();
            command("formatBlock", "h2");
          }}
        >
          Título
        </button>
        <button
          type="button"
          onMouseDown={(event) => {
            event.preventDefault();
            command("insertUnorderedList");
          }}
        >
          • Lista
        </button>
        <button
          type="button"
          onMouseDown={async (event) => {
            event.preventDefault();
            const selection = window.getSelection();
            const range = selection?.rangeCount ? selection.getRangeAt(0).cloneRange() : null;
            const result = await Swal.fire({
              icon: "question",
              title: "Agregar enlace",
              input: "url",
              inputLabel: "URL del enlace",
              inputPlaceholder: "https://…",
              showCancelButton: true,
              confirmButtonText: "Agregar",
              cancelButtonText: "Cancelar",
              confirmButtonColor: "#169c98",
              cancelButtonColor: "#708090",
              inputValidator: (value) => value ? undefined : "Ingresá una URL.",
            });
            if (!result.isConfirmed || !result.value) return;
            if (range && editor.current?.contains(range.commonAncestorContainer)) {
              selection?.removeAllRanges();
              selection?.addRange(range);
            }
            command("createLink", String(result.value));
          }}
        >
          Enlace
        </button>
        {([['aceptar', 'Aceptar invitación'], ['rechazar', 'No participar']] as const).map(([action, label]) => (
          <button key={action} type="button" onMouseDown={(event) => {
            event.preventDefault();
            command("insertHTML", `<a href="{${action}_invitacion}">${label}</a>&nbsp;`);
          }}>{label}</button>
        ))}
        <button
          type="button"
          onMouseDown={(event) => {
            event.preventDefault();
            command("removeFormat");
          }}
        >
          Limpiar
        </button>
      </div>
      <div
        ref={editor}
        className="wysiwyg-editor"
        contentEditable
        data-no-translate
        suppressContentEditableWarning
        onInput={sync}
        onBlur={sync}
      />
      <small>Insertá “Aceptar invitación” o “No participar” donde quieras. Podés editar su texto dentro del mensaje. Cada enlace registra la respuesta del faculty; en test no modifica datos.</small>
      <small>
        {simultaneous
          ? "Para WhatsApp y Push se enviará automáticamente una versión en texto plano."
          : "La imagen de encabezado, los firmantes y el pie general se incorporan al enviar."}
      </small>
    </div>
  );
}

function plainTextToHtml(value: string) {
  return value
    ? `<p>${value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>")}</p>`
    : "<p><br></p>";
}
function sanitizeRichHtml(value: string) {
  const template = document.createElement("template");
  template.innerHTML = String(value || "");
  const allowedTags = new Set(["P", "BR", "STRONG", "B", "EM", "I", "U", "UL", "OL", "LI", "H1", "H2", "H3", "H4", "A", "BLOCKQUOTE"]);
  for (const node of Array.from(template.content.querySelectorAll("*"))) {
    if (!allowedTags.has(node.tagName)) {
      node.replaceWith(...Array.from(node.childNodes));
      continue;
    }
    const originalHref = node.tagName === "A" ? node.getAttribute("href") || "" : "";
    for (const attribute of Array.from(node.attributes)) node.removeAttribute(attribute.name);
    if (node.tagName === "A") {
      try {
        if (/^\{(?:aceptar|rechazar)_invitacion\}$/.test(originalHref)) {
          node.setAttribute("href", originalHref);
          continue;
        }
        if (!/^(https:\/\/|mailto:)/i.test(originalHref)) throw new Error("unsafe protocol");
        const parsed = new URL(originalHref, window.location.origin);
        if (!["https:", "mailto:"].includes(parsed.protocol)) throw new Error("unsafe protocol");
        (node as HTMLAnchorElement).href = parsed.protocol === "mailto:" ? originalHref : parsed.href;
        (node as HTMLAnchorElement).target = "_blank";
        (node as HTMLAnchorElement).rel = "noopener noreferrer";
      } catch {
        node.removeAttribute("href");
      }
    }
  }
  return template.innerHTML;
}
function htmlToPlainText(value: string) {
  const node = document.createElement("div");
  node.innerHTML = sanitizeRichHtml(value);
  node.querySelectorAll("a").forEach(link => {
    const href = link.getAttribute("href") || "";
    if (/^\{(?:aceptar|rechazar)_invitacion\}$/.test(href)) link.append(document.createTextNode(`: ${href}`));
  });
  return node.innerText.trim();
}

function communicationChannelLabel(channel: CommunicationChannel) {
  return channel === "email"
    ? "Email"
    : channel === "whatsapp"
      ? "WhatsApp"
      : "Push";
}

function Splash() {
  return (
    <div className="splash">
      <BrandLogo large />
      <span className="splash-spinner" aria-hidden="true" />
      <p>Validando eventos activos…</p>
    </div>
  );
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function normalizeSearch(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}
function fileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.readAsDataURL(file);
  });
}
